/* eslint-disable max-lines, max-lines-per-function, max-len, id-length, indent */
import React, {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { TableUtility } from '@utility/dynamic-table';
import dayjs from 'dayjs';
import { Box, ClickAwayListener, Popper } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import {
  appendServiceNameToURL,
  appendServicePortToURL,
} from '../../../../Api/resources/serviceURLs';
import { serviceName } from '../../../../Api/resources/serviceConstants';
import addIcon from '../../../../Assets/images/svg/dar_add.svg';
import editActionIcon from '../../../../Assets/images/svg/dar_edit.svg';
import deleteActionIcon from '../../../../Assets/images/svg/dar_delete.svg';
import calendarIcon from '../../../../Assets/images/svg/dar_calendar_blank_gray.svg';
import { useAuth } from '../../../../Contexts/AuthContext';
import { MODULE_NAMES } from '../../../../Constants/modules';
import './ResourceLoadingPlan.scss';

const RESOURCE_LOADING_BASE_PATH = '/v1/resource-loading-plan';

const getStoredAuthToken = () => localStorage.getItem('authToken') || '';

const isNumericDisplayValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value !== 'string') {
    return false;
  }

  const normalizedValue = value.trim().replace(/,/g, '');
  return /^-?\d+(\.\d+)?$/.test(normalizedValue) && Number.isFinite(Number(normalizedValue));
};

const formatIndianNumber = (value, fractionDigits = 2) => (
  Number(String(value).replace(/,/g, '')).toLocaleString('en-IN', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
);

const formatValue = (value, fractionDigits = 2) => {
  if (value === null || value === undefined || value === '') return '-';
  if (!isNumericDisplayValue(value)) return value;
  return formatIndianNumber(value, fractionDigits);
};

const buildProjectServiceApiUrl = (path) => {
  const serviceURL = appendServiceNameToURL(serviceName.PROJECT_MS);
  const servicePort = appendServicePortToURL(serviceName.PROJECT_MS);
  const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  if (process.env.NODE_ENV === 'production') {
    return `${baseUrl}${serviceURL}/api${path}`;
  }

  return `${baseUrl}:${servicePort}${serviceURL}/api${path}`;
};

const normalizeErrorMessage = (responseData, fallbackMessage) => (
  responseData?.message
  || responseData?.error
  || fallbackMessage
);

const mapMasterOptions = (rows = []) => (rows || []).map((row) => ({
  value: String(row?.id ?? ''),
  label: String(row?.label ?? ''),
}));

const normalizeExperienceLabel = (value) => String(value ?? '')
  .trim()
  .replace(/\s*(yrs?|years?)$/i, '')
  .replace(/\s*-\s*/g, '-')
  .replace(/\s*\+\s*$/g, '+')
  .replace(/\s+/g, ' ');

const getExperienceSortParts = (value) => {
  const label = normalizeExperienceLabel(value).toLowerCase();
  const compactLabel = label.replace(/[\s_-]/g, '');

  if (compactLabel === 'junior') return { group: 1, start: 0, end: 0 };
  if (compactLabel === 'midlevel') return { group: 1, start: 1, end: 1 };
  if (compactLabel === 'senior') return { group: 1, start: 2, end: 2 };

  const rangeMatch = label.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return { group: 0, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
  }

  const plusMatch = label.match(/^(\d+(?:\.\d+)?)\+$/);
  if (plusMatch) {
    return { group: 0, start: Number(plusMatch[1]), end: Number(plusMatch[1]) };
  }

  const numericMatch = label.match(/^(\d+(?:\.\d+)?)$/);
  if (numericMatch) {
    return { group: 2, start: Number(numericMatch[1]), end: Number(numericMatch[1]) };
  }

  return { group: 3, start: 0, end: 0 };
};

const sortExperienceOptions = (options = [], getLabel = (option) => option?.label ?? option) => (
  [...options].sort((first, second) => {
    const firstLabel = normalizeExperienceLabel(getLabel(first));
    const secondLabel = normalizeExperienceLabel(getLabel(second));
    const firstSort = getExperienceSortParts(firstLabel);
    const secondSort = getExperienceSortParts(secondLabel);

    return firstSort.group - secondSort.group
      || firstSort.start - secondSort.start
      || firstSort.end - secondSort.end
      || firstLabel.localeCompare(secondLabel, undefined, { numeric: true, sensitivity: 'base' });
  })
);

const EMPTY_RESOURCE_LOADING_DETAIL_FORM = {
  role: '',
  workExperience: '',
  location: '',
  skillsComments: '',
  fromDate: '',
  toDate: '',
  headCount: '',
  allocationPercent: '',
};

const createEmptyAllocationRow = () => ({
  fromDate: '',
  toDate: '',
  headCount: '',
  allocationPercent: '',
});

const RESOURCE_LOADING_DETAIL_MIN_VISIBLE_ROWS = 3;
const RESOURCE_LOADING_PERCENT_PRECISION = 4;
const RESOURCE_LOADING_SKELETON_ROWS = 3;
const RESOURCE_LOADING_SKELETON_COLUMN_LIMIT = 8;
const RESOURCE_LOADING_PERMISSION_ACTION_ALIASES = {
  view: 'read',
  add: 'create',
  edit: 'update',
};

const waitForNextPaint = () => new Promise((resolve) => {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    setTimeout(resolve, 0);
    return;
  }

  window.requestAnimationFrame(() => {
    setTimeout(resolve, 0);
  });
});

const normalizePermissionAction = (action = '') => {
  const normalizedAction = String(action || '')
    .trim()
    .toLowerCase();
  return RESOURCE_LOADING_PERMISSION_ACTION_ALIASES[normalizedAction] || normalizedAction;
};

const getPermissionActions = (permissions = {}, moduleName = '') => {
  const moduleCandidates = [
    moduleName,
    moduleName.replace(/-/g, '_'),
    moduleName.replace(/_/g, '-'),
  ];

  for (const candidate of moduleCandidates) {
    if (Array.isArray(permissions?.[candidate])) {
      return permissions[candidate].map(normalizePermissionAction);
    }
  }

  return [];
};

const hasPermissionAction = (permissions = {}, moduleName = '', action = '') => (
  getPermissionActions(permissions, moduleName).includes(normalizePermissionAction(action))
);

const sanitizeHeadCountInput = (value) => String(value ?? '').replace(/[^\d]/g, '');

const sanitizeAllocationPercentInput = (value) => {
  const normalizedValue = String(value ?? '').replace(/[^\d.]/g, '');
  const [wholePart = '', ...decimalParts] = normalizedValue.split('.');
  const decimalPart = decimalParts.join('').slice(0, 2);
  const limitedWholePart = wholePart.slice(0, 3);

  if (decimalParts.length > 0) {
    return `${limitedWholePart}.${decimalPart}`;
  }

  return limitedWholePart;
};

const toRoundedNumber = (value, fractionDigits = RESOURCE_LOADING_PERCENT_PRECISION) => Number(
  Number(value || 0).toFixed(fractionDigits),
);

const toEffectiveResourceCount = ({ headCount, allocationPercent }) => {
  const normalizedHeadCount = Number(headCount || 0);
  const normalizedAllocationPercent = Number(allocationPercent || 0);

  if (!Number.isFinite(normalizedHeadCount) || normalizedHeadCount <= 0) {
    return 0;
  }

  if (!Number.isFinite(normalizedAllocationPercent) || normalizedAllocationPercent <= 0) {
    return toRoundedNumber(normalizedHeadCount, 4);
  }

  // W columns show effective resources; hours multiply this by working days * 8.
  return toRoundedNumber(
    normalizedHeadCount * (normalizedAllocationPercent / 100),
    4,
  );
};

const toCleanDisplayNumber = (value, fractionDigits = 2) => String(
  Number(Number(value || 0).toFixed(fractionDigits)),
);

const mergeResourceLoadingAllocationsByDateRange = (allocations = []) => {
  const mergedAllocationsByRange = new Map();

  (allocations || []).forEach((allocation, allocationIndex) => {
    if (allocation?.placeholderRow) {
      return;
    }

    const fromDate = normalizeDateValue(allocation.fromDate);
    const toDate = normalizeDateValue(allocation.toDate);
    if (!fromDate || !toDate) {
      return;
    }

    const headCount = Number(allocation.headCount || 0);
    const effectiveResourceCount = toEffectiveResourceCount({
      headCount,
      allocationPercent: allocation.allocationPercent,
    });
    const dateRangeKey = `${fromDate}__${toDate}`;
    const existingAllocation = mergedAllocationsByRange.get(dateRangeKey);

    if (!existingAllocation) {
      mergedAllocationsByRange.set(dateRangeKey, {
        ...allocation,
        id: allocation.id ?? null,
        rowKey: allocation.rowKey || `allocation-${Date.now()}-${allocationIndex + 1}`,
        fromDate,
        toDate,
        headCount: String(headCount),
        allocationPercent: toCleanDisplayNumber(allocation.allocationPercent),
        numResources: effectiveResourceCount,
      });
      return;
    }

    const mergedHeadCount = Number(existingAllocation.headCount || 0) + headCount;
    const mergedEffectiveResources = Number(existingAllocation.numResources || 0) + effectiveResourceCount;
    const mergedAllocationPercent = mergedHeadCount > 0
      ? toCleanDisplayNumber((mergedEffectiveResources / mergedHeadCount) * 100)
      : '0';

    mergedAllocationsByRange.set(dateRangeKey, {
      ...existingAllocation,
      headCount: String(mergedHeadCount),
      allocationPercent: mergedAllocationPercent,
      numResources: toEffectiveResourceCount({
        headCount: mergedHeadCount,
        allocationPercent: mergedAllocationPercent,
      }),
    });
  });

  return [...mergedAllocationsByRange.values()];
};

const normalizeAllocationRowsForDraft = (allocations = []) => (allocations || [])
  .map((allocation, allocationIndex) => {
    const fallbackResourceCount = Number(allocation?.numResources ?? allocation?.value ?? 0);
    const headCount = allocation?.headCount ?? (fallbackResourceCount > 0 ? fallbackResourceCount : '');
    const allocationPercent = allocation?.allocationPercent ?? (fallbackResourceCount > 0 ? 100 : '');

    return {
      id: allocation?.id ?? null,
      fromDate: normalizeDateValue(allocation?.fromDate),
      toDate: normalizeDateValue(allocation?.toDate),
      headCount: String(headCount),
      allocationPercent: String(allocationPercent),
      numResources: toEffectiveResourceCount({ headCount, allocationPercent }),
      rowKey: allocation?.rowKey || `allocation-${allocationIndex + 1}`,
    };
  })
  .filter((allocation) => (
    allocation.fromDate
    && allocation.toDate
    && Number(allocation.headCount) > 0
    && Number(allocation.allocationPercent) > 0
    && Number(allocation.numResources) > 0
  ));

const buildResourceLoadingDraftPayloadRows = (rows = []) => rows
  .filter((row) => !row?.isDeleted && !row?.isTotalRow)
  .map((row) => ({
    loadingId: row?.loadingId ?? row?.id ?? null,
    roleId: row?.roleId ?? null,
    experienceId: row?.experienceId ?? null,
    locationId: row?.locationId ?? null,
    skillsComments: row?.skillsComments ?? row?.skill ?? row?.comments ?? '',
    weeklyAllocations: normalizeAllocationRowsForDraft(
      row?.allocations
      || row?.weeklyAllocations
      || [],
    ).map((allocation) => ({
      fromDate: allocation.fromDate,
      toDate: allocation.toDate,
      headCount: Number(allocation.headCount),
      allocationPercent: Number(allocation.allocationPercent),
      numResources: allocation.numResources,
    })),
  }));

const getResourceLoadingDetailRowKey = (row = {}, fallbackIndex = 0) => (
  [
    row?.id,
    row?.resourceLoadingDetailId,
    row?.detailId,
    row?.role,
    row?.workExperience,
    row?.location,
    fallbackIndex,
  ].filter((value) => value !== null && value !== undefined && value !== '').join('__')
);

const normalizeDateValue = (value) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const formatDateForDisplay = (value) => {
  const normalizedValue = normalizeDateValue(value);
  return normalizedValue ? dayjs(normalizedValue).format('DD-MMM-YYYY') : '-';
};

const formatAllocationPercentForDisplay = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '-';
  }

  return `${toCleanDisplayNumber(numericValue)}%`;
};

const getResourceLoadingAllocationBreakdownRows = (row = {}) => {
  const sourceAllocations = Array.isArray(row?.allocations) && row.allocations.length > 0
    ? row.allocations
    : row?.fromDate || row?.toDate || row?.headCount || row?.allocationPercent
      ? [row]
      : [];

  return sourceAllocations
    .filter((allocation) => !allocation?.placeholderRow)
    .map((allocation) => ({
      fromDate: normalizeDateValue(allocation?.fromDate),
      toDate: normalizeDateValue(allocation?.toDate),
      headCount: allocation?.headCount ?? allocation?.headcount ?? '',
      allocationPercent: allocation?.allocationPercent ?? allocation?.allocation ?? '',
    }))
    .filter((allocation) => allocation.fromDate || allocation.toDate);
};

const getAllocationOverlapReason = ({
  fromDate,
  toDate,
  existingFromDate,
  existingToDate,
}) => {
  const startsInsideExisting = fromDate >= existingFromDate && fromDate <= existingToDate;
  const endsInsideExisting = toDate >= existingFromDate && toDate <= existingToDate;
  const selectedRangeInsideExisting = fromDate >= existingFromDate && toDate <= existingToDate;
  const selectedRangeCoversExisting = fromDate <= existingFromDate && toDate >= existingToDate;

  if (fromDate === existingFromDate && toDate === existingToDate) {
    return 'same-range';
  }
  if (selectedRangeInsideExisting) {
    return 'inside-existing';
  }
  if (selectedRangeCoversExisting) {
    return 'covers-existing';
  }
  if (startsInsideExisting) {
    return 'from-date-overlap';
  }
  if (endsInsideExisting) {
    return 'to-date-overlap';
  }

  return 'overlap';
};

const findOverlappingAllocationRange = ({
  allocations = [],
  fromDate,
  toDate,
  ignoredIndex = null,
}) => {
  const normalizedFromDate = normalizeDateValue(fromDate);
  const normalizedToDate = normalizeDateValue(toDate);

  if (!normalizedFromDate || !normalizedToDate) {
    return null;
  }

  const overlappingAllocation = (allocations || []).find((allocationRow, allocationIndex) => {
    if (allocationRow?.placeholderRow || allocationIndex === ignoredIndex) {
      return false;
    }

    const existingFromDate = normalizeDateValue(allocationRow?.fromDate);
    const existingToDate = normalizeDateValue(allocationRow?.toDate);

    if (!existingFromDate || !existingToDate) {
      return false;
    }

    return normalizedFromDate <= existingToDate && normalizedToDate >= existingFromDate;
  }) || null;

  if (!overlappingAllocation) {
    return null;
  }

  const existingFromDate = normalizeDateValue(overlappingAllocation?.fromDate);
  const existingToDate = normalizeDateValue(overlappingAllocation?.toDate);

  return {
    allocation: overlappingAllocation,
    reason: getAllocationOverlapReason({
      fromDate: normalizedFromDate,
      toDate: normalizedToDate,
      existingFromDate,
      existingToDate,
    }),
  };
};

const buildAllocationOverlapMessage = (overlapDetails = {}) => {
  const allocationRow = overlapDetails.allocation || {};
  const existingRangeLabel = `${formatDateForDisplay(allocationRow.fromDate)} to ${formatDateForDisplay(allocationRow.toDate)}`;

  switch (overlapDetails.reason) {
    case 'same-range':
      return `This exact date range already exists (${existingRangeLabel}). Please choose a different range.`;
    case 'inside-existing':
      return `Selected date range falls inside existing allocation range ${existingRangeLabel}. Please choose a non-overlapping range.`;
    case 'covers-existing':
      return `Selected date range includes existing allocation range ${existingRangeLabel}. Please choose a non-overlapping range.`;
    case 'from-date-overlap':
      return `From date falls within existing allocation range ${existingRangeLabel}. Please choose a start date outside that range.`;
    case 'to-date-overlap':
      return `To date falls within existing allocation range ${existingRangeLabel}. Please choose an end date outside that range.`;
    default:
      return `Selected date range overlaps with existing allocation range ${existingRangeLabel}. Please choose a non-overlapping range.`;
  }
};

const getAllocationOverlapFieldErrors = (overlapDetails = {}) => {
  switch (overlapDetails.reason) {
    case 'from-date-overlap':
      return { fromDate: 'From date overlaps with an existing allocation' };
    case 'to-date-overlap':
      return { toDate: 'To date overlaps with an existing allocation' };
    default:
      return {
        fromDate: 'Date range overlaps with an existing allocation',
        toDate: 'Date range overlaps with an existing allocation',
      };
  }
};

const parseToolbarBoundaryDate = (value) => {
  const parsed = value ? dayjs(value).startOf('day') : null;
  return parsed && parsed.isValid() ? parsed : null;
};

const isToolbarDateOutOfRange = (date, minDate, maxDate) => {
  const selectedDate = date && date.isValid ? date.startOf('day') : null;
  const minBoundary = parseToolbarBoundaryDate(minDate);
  const maxBoundary = parseToolbarBoundaryDate(maxDate);

  if (!selectedDate || !selectedDate.isValid()) {
    return true;
  }
  if (minBoundary && selectedDate.isBefore(minBoundary, 'day')) {
    return true;
  }
  if (maxBoundary && selectedDate.isAfter(maxBoundary, 'day')) {
    return true;
  }

  return false;
};

const parseWeekFieldDates = (field = '') => {
  const matches = String(field).match(/\d{4}_\d{2}_\d{2}/g) || [];
  if (matches.length < 2) {
    return null;
  }

  return {
    fromDate: matches[0].replace(/_/g, '-'),
    toDate: matches[1].replace(/_/g, '-'),
  };
};

const countFrontendWorkingDays = (fromDateInput, toDateInput) => {
  const start = dayjs(fromDateInput);
  const end = dayjs(toDateInput);
  if (!start.isValid() || !end.isValid() || end.isBefore(start, 'day')) {
    return 0;
  }

  let days = 0;
  let cursor = start.startOf('day');
  const finalDay = end.startOf('day');
  while (!cursor.isAfter(finalDay, 'day')) {
    const day = cursor.day();
    if (day !== 0 && day !== 6) {
      days += 1;
    }
    cursor = cursor.add(1, 'day');
  }
  return days;
};

const calculateFrontendAllocationEffort = ({
  fromDate,
  toDate,
  numResources,
  filterStartDate,
  filterEndDate,
}) => {
  const allocationStart = dayjs(fromDate);
  const allocationEnd = dayjs(toDate);
  const filterStart = dayjs(filterStartDate);
  const filterEnd = dayjs(filterEndDate);

  if (
    !allocationStart.isValid()
    || !allocationEnd.isValid()
    || !filterStart.isValid()
    || !filterEnd.isValid()
  ) {
    return 0;
  }

  const overlapStart = allocationStart.isAfter(filterStart, 'day') ? allocationStart : filterStart;
  const overlapEnd = allocationEnd.isBefore(filterEnd, 'day') ? allocationEnd : filterEnd;
  const workingDays = countFrontendWorkingDays(overlapStart, overlapEnd);
  return Number((workingDays * 8 * Number(numResources || 0)).toFixed(2));
};

const normalizeResourceLoadingDetailRowsWithKeys = (rows = []) => (
  rows.map((row, rowIndex) => ({
    ...row,
    isDeleted: Boolean(row?.isDeleted || row?.isDeleted || row?.is_deleted),
    rowKey: row?.rowKey || getResourceLoadingDetailRowKey(row, rowIndex),
  }))
);

const toResourceLoadingDetailFormState = (row = {}) => {
  const hasExistingAllocationValues = (
    Array.isArray(row?.allocations)
    && row.allocations.length > 0
  ) || Boolean(
    row?.fromDate
    || row?.toDate
    || row?.headCount
    || row?.headcount
    || row?.allocationPercent
    || row?.allocation,
  );

  const allocations = Array.isArray(row?.allocations) && row.allocations.length > 0
    ? row.allocations.map((allocationRow) => ({
      role: String(row?.roleId ?? row?.resource_role_id ?? row?.role ?? ''),
      workExperience: String(
        row?.experienceId
        ?? row?.resource_exp_id
        ?? row?.workExperience
        ?? row?.experience
        ?? '',
      ),
      location: String(row?.locationId ?? row?.resource_loc_id ?? row?.location ?? ''),
      skillsComments: String(row?.skillsComments ?? row?.skill ?? row?.comments ?? ''),
      fromDate: normalizeDateValue(allocationRow?.fromDate),
      toDate: normalizeDateValue(allocationRow?.toDate),
      headCount: String(allocationRow?.headCount ?? allocationRow?.headcount ?? ''),
      allocationPercent: String(allocationRow?.allocationPercent ?? allocationRow?.allocation ?? ''),
    }))
    : hasExistingAllocationValues
      ? [
        {
          role: String(row?.roleId ?? row?.resource_role_id ?? row?.role ?? ''),
          workExperience: String(
            row?.experienceId
            ?? row?.resource_exp_id
            ?? row?.workExperience
            ?? row?.experience
            ?? '',
          ),
          location: String(row?.locationId ?? row?.resource_loc_id ?? row?.location ?? ''),
          skillsComments: String(row?.skillsComments ?? row?.skill ?? row?.comments ?? ''),
          fromDate: normalizeDateValue(row?.fromDate),
          toDate: normalizeDateValue(row?.toDate),
          headCount: String(row?.headCount ?? row?.headcount ?? ''),
          allocationPercent: String(row?.allocationPercent ?? row?.allocation ?? ''),
        },
      ]
      : [];

  return {
    form: {
      ...EMPTY_RESOURCE_LOADING_DETAIL_FORM,
      role: String(row?.roleId ?? row?.resource_role_id ?? row?.role ?? ''),
      workExperience: String(
        row?.experienceId
        ?? row?.resource_exp_id
        ?? row?.workExperience
        ?? row?.experience
        ?? '',
      ),
      location: String(row?.locationId ?? row?.resource_loc_id ?? row?.location ?? ''),
      skillsComments: String(row?.skillsComments ?? row?.skill ?? row?.comments ?? ''),
      fromDate: allocations[0]?.fromDate || '',
      toDate: allocations[0]?.toDate || '',
      headCount: allocations[0]?.headCount || '',
      allocationPercent: allocations[0]?.allocationPercent || '',
    },
    allocations,
  };
};

const buildToolbarOnlyConfiguration = ({
  heading,
  additionalElements = [],
}) => ({
  generalSettings: {
    enableApiFiltering: false,
    showToolBar: true,
    showPagination: false,
    baseURL: '',
    headers: {},
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: false },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: {
        endpoint: '',
        enabled: false,
      },
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: false,
      showDownloadCsv: false,
      showColumnCustomize: false,
      showColumnFilterMenu: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: false,
      showToolBarHeading: true,
      toolBarHeading: heading,
      additionalElements,
    },
  },
  tableSettings: {
    size: 'small',
    tableConfig: [],
    tableScrollHeight: 'auto',
    loaderSkeleton: {
      rows: 0,
      bodyCells: 0,
      columns: 0,
    },
    paginationConfig: {
      page: 1,
      limit: 10,
    },
    showExpandCollapse: false,
    maxFrozenColumns: {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
  },
});

const buildReadOnlyTableConfiguration = (
  tableConfig,
  {
    tableScrollHeight = 'auto',
    resizableColumns = false,
    maxFrozenColumns = {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    },
  } = {},
) => ({
  generalSettings: {
    enableApiFiltering: false,
    showToolBar: false,
    showPagination: false,
    baseURL: '',
    headers: {},
    apiEndpoints: {
      getAllViews: { endpoint: '', enabled: false },
      getFilteredData: { endpoint: '', enabled: false },
      saveOrUpdateView: { endpoint: '', enabled: false },
      deleteView: { endpoint: '', enabled: false },
      download: { endpoint: '', enabled: false },
    },
    toolBarSettings: {
      showAddNew: false,
      showDownloadExcel: false,
      showColumnCustomize: false,
      showColumnFilterMenu: false,
      showHelp: false,
      showSearch: false,
      showSaveView: false,
      showReset: false,
      showExpandCollapse: false,
      showFilterOption: false,
      showToolBarHeading: false,
      toolBarHeading: '',
    },
  },
  tableSettings: {
    size: 'small',
    tableConfig,
    pageSize: 1000,
    tableScrollHeight,
    loaderSkeleton: {
      rows: RESOURCE_LOADING_SKELETON_ROWS,
      bodyCells: 1,
      columns: Math.min(Math.max(tableConfig.length, 1), RESOURCE_LOADING_SKELETON_COLUMN_LIMIT),
    },
    paginationConfig: {
      page: 1,
      limit: 1000,
    },
    showExpandCollapse: false,
    resizableColumns,
    maxFrozenColumns,
  },
});

const isResourceLoadingTotalRow = (row = {}) => (
  Boolean(row?.isTotalRow)
  || String(row?.id || '')
    .toLowerCase() === 'total-row'
  || String(row?.loadingId || '')
    .toLowerCase() === 'total-row'
  || String(row?.role || '')
    .trim()
    .toLowerCase() === 'total'
);

const isResourceLoadingDataRow = (row = {}) => !row?.isDeleted && !isResourceLoadingTotalRow(row);

const getResourceLoadingWeekFields = (columns = []) => (columns || []).flatMap((column) => {
  if (Array.isArray(column.children) && column.children.length > 0) {
    return getResourceLoadingWeekFields(column.children);
  }

  return String(column.field || '').startsWith('week_') ? [column.field] : [];
});

const getPlainColumnHeader = (column = {}) => {
  if (Array.isArray(column.subHeaders) && column.subHeaders.length > 0) {
    return [
      column.header,
      ...column.subHeaders,
    ].filter(Boolean).join('\n');
  }

  return String(column.header || column.field || '').trim();
};

const isBlankExportValue = (value) => value === null || value === undefined || value === '';

const getResourceLoadingExportColumns = (columns = []) => (columns || []).flatMap((column) => {
  if (Array.isArray(column.children) && column.children.length > 0) {
    return getResourceLoadingExportColumns(column.children);
  }

  if (!column?.field || column.field === 'action' || column.visible === false) {
    return [];
  }

  const isNumericColumn = column.align === 'right'
    || column.field === 'totalEfforts'
    || String(column.field).startsWith('week_');

  return [{
    field: column.field,
    key: column.field,
    header: getPlainColumnHeader(column),
    numeric: isNumericColumn,
    fractionDigits: column.fractionDigits,
  }];
});

const buildResourceLoadingAllocationExportValue = (row = {}) => (
  getResourceLoadingAllocationBreakdownRows(row)
    .map((allocation) => (
      `${allocation.headCount || '-'} - ${formatAllocationPercentForDisplay(allocation.allocationPercent)} - ${formatDateForDisplay(allocation.fromDate)} - ${formatDateForDisplay(allocation.toDate)}`
    ))
    .join('\n') || '-'
);

const buildResourceLoadingExportPayload = (
  rows = [],
  columns = [],
  {
    formatAllocationBreakdown = false,
    preserveBlankNumericCells = false,
  } = {},
) => {
  const baseExportColumns = getResourceLoadingExportColumns(columns);
  const blankNumericFields = new Set(
    preserveBlankNumericCells
      ? baseExportColumns
        .filter((column) => (
          column.numeric
          && (rows || []).some((row) => isBlankExportValue(row?.[column.field]))
        ))
        .map((column) => column.field)
      : [],
  );
  const exportColumns = baseExportColumns.map((column) => (
    blankNumericFields.has(column.field)
      ? { ...column, numeric: false }
      : column
  ));

  return {
    tableConfiguration: exportColumns,
    tableData: (rows || []).map((row) => (
      exportColumns.reduce((exportRow, column) => {
        if (formatAllocationBreakdown && column.field === 'allocationBreakdown') {
          exportRow[column.field] = row?.isTotalRow ? '' : buildResourceLoadingAllocationExportValue(row);
          return exportRow;
        }

        const value = row?.[column.field];
        if (React.isValidElement(value)) {
          exportRow[column.field] = '';
          return exportRow;
        }

        if (blankNumericFields.has(column.field)) {
          exportRow[column.field] = isBlankExportValue(value)
            ? ''
            : formatValue(value, column.fractionDigits);
          return exportRow;
        }

        exportRow[column.field] = value ?? '';
        return exportRow;
      }, {})
    )),
  };
};

const buildResourceLoadingDetailsExportPayload = (rows = [], columns = []) => (
  buildResourceLoadingExportPayload(rows, columns, { formatAllocationBreakdown: true })
);

const buildResourceLoadingDetailExportRows = (rows = [], weekFields = []) => {
  const totalWeekValues = {};

  weekFields.forEach((field) => {
    totalWeekValues[field] = 0;
  });

  const exportRows = rows.map((row) => {
    if (row.isTotalRow) {
      return row;
    }

    weekFields.forEach((field) => {
      const num = Number(row[field]);
      totalWeekValues[field] += Number.isFinite(num) ? num : 0;
    });

    return { ...row };
  });

  const hasTotalRow = exportRows.some((r) => r.isTotalRow);
  if (hasTotalRow || exportRows.length === 0) {
    return exportRows;
  }

  const grandTotalEfforts = Number(
    rows
      .reduce((sum, r) => sum + (Number(r.totalEfforts) || 0), 0)
      .toFixed(2),
  );

  return [
    ...exportRows,
    {
      id: 'total-row',
      loadingId: 'total-row',
      role: 'Total',
      workExperience: '',
      location: '',
      allocationBreakdown: '',
      skillsComments: '',
      totalEfforts: grandTotalEfforts,
      persistedTotalEfforts: grandTotalEfforts,
      isTotalRow: true,
      isBottomFrozen: true,
      rowConfiguration: { isFrozen: true },
      ...totalWeekValues,
    },
  ];
};

const buildResourceLoadingExportTableConfiguration = ({
  columns = [],
  sheetName,
  fileName,
  reportTitle,
  transformResponse,
}) => {
  const configuration = buildReadOnlyTableConfiguration(columns, { tableScrollHeight: '1px' });

  return {
    ...configuration,
    generalSettings: {
      ...configuration.generalSettings,
      apiEndpoints: {
        ...configuration.generalSettings.apiEndpoints,
        download: {
          endpoint: '',
          enabled: false,
          filename: fileName,
          sheetName,
          reportTitle,
          transformResponse,
        },
      },
      toolBarSettings: {
        ...configuration.generalSettings.toolBarSettings,
        toolBarHeading: reportTitle,
      },
    },
    tableSettings: {
      ...configuration.tableSettings,
      tableClassName: 'resource-loading-plan-export-only-table',
    },
  };
};

const enrichWeekColumnHeaders = (columns = []) => columns.map((column) => {
  if (Array.isArray(column.children) && column.children.length > 0) {
    return {
      ...column,
      children: enrichWeekColumnHeaders(column.children),
    };
  }

  if (Array.isArray(column.subHeaders) && column.subHeaders.length > 0) {
    const [fromDateLabel = '', toDateLabel = ''] = column.subHeaders;
    const { subHeaders: __consumedSubHeaders, ...weekColumn } = column;

    return {
      ...weekColumn,
      header: (
        <div className="resource-loading-week-header-cell">
          <div className="resource-loading-week-header-cell__line resource-loading-week-header-cell__line--week">
            {column.header}
          </div>
          <div className="resource-loading-week-header-cell__line resource-loading-week-header-cell__line--start-date">
            {fromDateLabel}
          </div>
          <div className="resource-loading-week-header-cell__line resource-loading-week-header-cell__line--end-date">
            {toDateLabel}
          </div>
        </div>
      ),
    };
  }

  return column;
});

const ToolbarDateInput = ({
  ariaLabel,
  placeholder,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const parsedValue = value ? dayjs(value, 'YYYY-MM-DD', true) : null;
  const minBoundary = parseToolbarBoundaryDate(minDate);
  const maxBoundary = parseToolbarBoundaryDate(maxDate);
  const displayValue = parsedValue && parsedValue.isValid()
    ? parsedValue.format('DD-MM-YYYY')
    : placeholder;

  const handleDateChange = (date) => {
    if (!date || !date.isValid()) {
      onChange('');
      setOpen(false);
      return;
    }

    if (isToolbarDateOutOfRange(date, minDate, maxDate)) {
      return;
    }

    onChange(date.format('YYYY-MM-DD'));
    setOpen(false);
  };

  return (
    <div className="toolbar-date-picker">
      <button
        ref={anchorRef}
        type="button"
        className="toolbar-date-picker__button"
        onClick={() => {
          if (disabled) {
            return;
          }
          setOpen(true);
        }}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        <span
          className={[
            'toolbar-date-picker__value',
            parsedValue && parsedValue.isValid() ? 'is-selected' : 'is-placeholder',
          ].join(' ').trim()}
        >
          {displayValue}
        </span>
        <span className="toolbar-date-picker__icon" aria-hidden="true">
          <img src={calendarIcon} alt="" />
        </span>
      </button>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className="toolbar-date-picker__popper"
          style={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ padding: 1, bgcolor: '#fff', borderRadius: '8px' }}>
              <DateCalendar
                value={parsedValue && parsedValue.isValid() ? parsedValue : null}
                onChange={handleDateChange}
                minDate={minBoundary || undefined}
                maxDate={maxBoundary || undefined}
                shouldDisableDate={(date) => isToolbarDateOutOfRange(date, minDate, maxDate)}
                dayOfWeekFormatter={(day) => day.format('ddd')}
              />
            </Box>
          </ClickAwayListener>
        </Popper>
      </LocalizationProvider>
    </div>
  );
};

const ResourceLoadingModalDateInput = ({
  ariaLabel,
  value,
  placeholder,
  onChange,
  minDate,
  maxDate,
  referenceDate,
  hasError,
}) => {
  const [open, setOpen] = useState(false);
  const [visibleDate, setVisibleDate] = useState(null);
  const [isMonthMenuOpen, setIsMonthMenuOpen] = useState(false);
  const anchorRef = useRef(null);

  const parsedValue = value ? dayjs(value, 'YYYY-MM-DD', true) : null;
  const minBoundary = parseToolbarBoundaryDate(minDate);
  const maxBoundary = parseToolbarBoundaryDate(maxDate);
  const referenceBoundary = parseToolbarBoundaryDate(referenceDate) || minBoundary || maxBoundary;
  const calendarValue = parsedValue && parsedValue.isValid() ? parsedValue : referenceBoundary;
  const activeVisibleDate = visibleDate && visibleDate.isValid()
    ? visibleDate
    : (calendarValue || dayjs()).startOf('month');
  const displayValue = parsedValue && parsedValue.isValid()
    ? parsedValue.format('DD-MM-YYYY')
    : placeholder;
  const yearOptions = useMemo(() => {
    const anchorYear = (referenceBoundary || calendarValue || dayjs()).year();
    const startYear = minBoundary ? minBoundary.year() : anchorYear - 5;
    const endYear = maxBoundary ? maxBoundary.year() : anchorYear + 5;

    return Array.from(
      { length: Math.max(endYear - startYear + 1, 1) },
      (_, index) => startYear + index,
    );
  }, [calendarValue, maxBoundary, minBoundary, referenceBoundary]);
  const monthOptions = useMemo(() => ([
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]), []);
  const weekdayLabels = useMemo(() => ['S', 'M', 'T', 'W', 'T', 'F', 'S'], []);
  const calendarCells = useMemo(() => {
    const monthStart = activeVisibleDate.startOf('month');
    const leadingBlankCount = monthStart.day();
    const daysInMonth = activeVisibleDate.daysInMonth();
    const cells = Array.from({ length: leadingBlankCount }, (_, index) => ({
      key: `blank-start-${index}`,
      blank: true,
    }));

    for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber += 1) {
      const dayDate = activeVisibleDate.date(dayNumber).startOf('day');
      cells.push({
        key: dayDate.format('YYYY-MM-DD'),
        date: dayDate,
        dayNumber,
      });
    }

    const trailingBlankCount = (7 - (cells.length % 7)) % 7;
    return [
      ...cells,
      ...Array.from({ length: trailingBlankCount }, (_, index) => ({
        key: `blank-end-${index}`,
        blank: true,
      })),
    ];
  }, [activeVisibleDate]);

  const handleDateChange = (date) => {
    if (!date || !date.isValid()) {
      onChange('');
      setOpen(false);
      return;
    }

    if (isToolbarDateOutOfRange(date, minDate, maxDate)) {
      return;
    }

    onChange(date.format('YYYY-MM-DD'));
    setOpen(false);
  };
  const handleOpenCalendar = () => {
    setVisibleDate((calendarValue || dayjs()).startOf('month'));
    setOpen(true);
  };
  const isMonthDisabled = (monthIndex, year = activeVisibleDate.year()) => {
    const monthStart = activeVisibleDate
      .year(year)
      .month(monthIndex)
      .startOf('month');
    const monthEnd = monthStart.endOf('month');

    return Boolean(
      (minBoundary && monthEnd.isBefore(minBoundary, 'day'))
      || (maxBoundary && monthStart.isAfter(maxBoundary, 'day')),
    );
  };
  const handleMonthSelect = (monthIndex) => {
    if (isMonthDisabled(monthIndex)) {
      return;
    }

    setVisibleDate(activeVisibleDate.month(monthIndex).startOf('month'));
    setIsMonthMenuOpen(false);
  };
  const handleYearChange = (event) => {
    const nextYear = Number(event.target.value);
    const currentMonth = activeVisibleDate.month();
    const nextMonth = isMonthDisabled(currentMonth, nextYear)
      ? monthOptions.findIndex((_, monthIndex) => !isMonthDisabled(monthIndex, nextYear))
      : currentMonth;

    setVisibleDate(activeVisibleDate
      .year(nextYear)
      .month(Math.max(nextMonth, 0))
      .startOf('month'));
  };
  const isHighlightedDate = (date) => (
    calendarValue
    && calendarValue.isValid()
    && date.isSame(calendarValue, 'day')
  );

  return (
    <div className="resource-loading-detail-modal__date-picker">
      <button
        ref={anchorRef}
        type="button"
        className={[
          'resource-loading-detail-modal__date-button',
          hasError ? 'is-error' : '',
        ].filter(Boolean).join(' ')}
        onClick={handleOpenCalendar}
        aria-label={ariaLabel}
      >
        <span
          className={[
            'resource-loading-detail-modal__date-value',
            parsedValue && parsedValue.isValid() ? 'is-selected' : 'is-placeholder',
          ].filter(Boolean).join(' ')}
        >
          {displayValue}
        </span>
        <span className="resource-loading-detail-modal__date-icon" aria-hidden="true">
          <img src={calendarIcon} alt="" />
        </span>
      </button>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          className="resource-loading-detail-modal__date-popper"
          style={{ zIndex: 20000 }}
          disablePortal
        >
          <ClickAwayListener onClickAway={() => {
            setOpen(false);
            setIsMonthMenuOpen(false);
          }}
          >
            <div className="resource-loading-modal-calendar">
              <div className="resource-loading-modal-calendar__header">
                <div className="resource-loading-modal-calendar__month-menu">
                  <button
                    type="button"
                    className="resource-loading-modal-calendar__select resource-loading-modal-calendar__month-trigger"
                    onClick={() => setIsMonthMenuOpen((current) => !current)}
                    aria-label="Select month"
                    aria-expanded={isMonthMenuOpen}
                  >
                    {monthOptions[activeVisibleDate.month()]}
                  </button>
                  {isMonthMenuOpen ? (
                    <div className="resource-loading-modal-calendar__month-options">
                      {monthOptions.map((monthLabel, monthIndex) => (
                        <button
                          key={monthLabel}
                          type="button"
                          className={[
                            'resource-loading-modal-calendar__month-option',
                            monthIndex === activeVisibleDate.month() ? 'is-selected' : '',
                          ].filter(Boolean).join(' ')}
                          disabled={isMonthDisabled(monthIndex)}
                          onClick={() => handleMonthSelect(monthIndex)}
                        >
                          {monthLabel}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <select
                  className="resource-loading-modal-calendar__select"
                  value={activeVisibleDate.year()}
                  onChange={handleYearChange}
                  aria-label="Select year"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="resource-loading-modal-calendar__weekdays">
                {weekdayLabels.map((weekdayLabel, weekdayIndex) => (
                  <span key={`${weekdayLabel}-${weekdayIndex}`}>{weekdayLabel}</span>
                ))}
              </div>
              <div className="resource-loading-modal-calendar__grid">
                {calendarCells.map((cell) => (
                  cell.blank ? (
                    <span
                      key={cell.key}
                      className="resource-loading-modal-calendar__blank"
                    />
                  ) : (
                    <button
                      key={cell.key}
                      type="button"
                      className={[
                        'resource-loading-modal-calendar__day',
                        isHighlightedDate(cell.date) ? 'is-selected' : '',
                      ].filter(Boolean).join(' ')}
                      disabled={isToolbarDateOutOfRange(cell.date, minDate, maxDate)}
                      onClick={() => handleDateChange(cell.date)}
                    >
                      {cell.dayNumber}
                    </button>
                  )
                ))}
              </div>
            </div>
          </ClickAwayListener>
        </Popper>
      </LocalizationProvider>
    </div>
  );
};

ResourceLoadingModalDateInput.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  referenceDate: PropTypes.string,
  hasError: PropTypes.bool,
};

ResourceLoadingModalDateInput.defaultProps = {
  minDate: null,
  maxDate: null,
  referenceDate: null,
  hasError: false,
};

const ResourceLoadingPlanToolbarControls = ({
  fromDate,
  toDate,
  onChangeFromDate,
  onChangeToDate,
  onApply,
  fromMinDate,
  fromMaxDate,
  toMinDate,
  toMaxDate,
  dateDisabled = false,
  applyDisabled = false,
  exportElement = null,
}) => (
  <div className="resource-loading-toolbar-controls">
    <ToolbarDateInput
      ariaLabel="From date"
      placeholder="From"
      value={fromDate}
      onChange={onChangeFromDate}
      minDate={fromMinDate}
      maxDate={fromMaxDate}
      disabled={dateDisabled}
    />
    <span className="date-separator">-</span>
    <ToolbarDateInput
      ariaLabel="To date"
      placeholder="To"
      value={toDate}
      onChange={onChangeToDate}
      minDate={toMinDate}
      maxDate={toMaxDate}
      disabled={dateDisabled}
    />
    <button
      type="button"
      className="resource-loading-btn resource-loading-btn-primary"
      onClick={applyDisabled ? undefined : onApply}
      disabled={applyDisabled}
    >
      Apply
    </button>
    {exportElement}
  </div>
);

const ResourceLoadingPlanMultiExportButton = ({ tables }) => {
  const exportButtonWrapperRef = useRef(null);

  useEffect(() => {
    const wrapperElement = exportButtonWrapperRef.current;

    if (!wrapperElement) {
      return undefined;
    }

    const updateExportTooltip = () => {
      const exportButton = wrapperElement.querySelector('[data-pr-tooltip]');

      if (exportButton) {
        exportButton.setAttribute('data-pr-tooltip', 'Download Excel');
        exportButton.setAttribute('aria-label', 'Download Excel');
        exportButton.setAttribute('title', 'Download Excel');
      }
    };

    updateExportTooltip();
    const tooltipUpdateIds = [
      window.setTimeout(updateExportTooltip, 0),
      window.setTimeout(updateExportTooltip, 100),
    ];
    const tooltipObserver = new MutationObserver(updateExportTooltip);
    tooltipObserver.observe(wrapperElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-pr-tooltip', 'aria-label', 'title'],
    });

    return () => {
      tooltipUpdateIds.forEach((tooltipUpdateId) => window.clearTimeout(tooltipUpdateId));
      tooltipObserver.disconnect();
    };
  }, [tables]);

  if (!Array.isArray(tables) || tables.length === 0) {
    return null;
  }

  return (
    <div className="resource-loading-multi-export" ref={exportButtonWrapperRef}>
      <TableUtility tables={tables} showMultiTableExport />
    </div>
  );
};

const ResourceLoadingDetailsToolbar = React.memo(({
  canViewApprovalHistory,
  canAddResourceLoadingDetail,
  isLocked,
  onOpenApprovalHistory,
  onAddResourceLoadingDetail,
}) => {
  const toolbarHeading = useMemo(
    () => (
      <div className="resource-loading-details-toolbar-heading">
        <span className="resource-loading-details-toolbar-heading__title">
          Resource Loading Details
        </span>
        <div className="resource-loading-details-toolbar-heading__actions">
          {canViewApprovalHistory ? (
            <button
              type="button"
              className="resource-loading-approval-link"
              onClick={onOpenApprovalHistory}
            >
              Approval History
            </button>
          ) : null}
          <button
            type="button"
            className={`resource-loading-add-detail-btn ${(isLocked || !canAddResourceLoadingDetail) ? 'is-disabled' : ''}`}
            aria-label="Add resource loading detail"
            title="Add"
            onClick={(isLocked || !canAddResourceLoadingDetail) ? undefined : onAddResourceLoadingDetail}
            disabled={Boolean(isLocked || !canAddResourceLoadingDetail)}
          >
            <img src={addIcon} alt="" aria-hidden="true" />
          </button>
        </div>
      </div>
    ),
    [
      canAddResourceLoadingDetail,
      canViewApprovalHistory,
      onAddResourceLoadingDetail,
      onOpenApprovalHistory,
    ],
  );

  const toolbarConfiguration = useMemo(
    () => buildToolbarOnlyConfiguration({
      heading: toolbarHeading,
    }),
    [toolbarHeading],
  );

  return (
    <div className="resource-loading-plan-table-utility resource-loading-plan-table-utility--details-toolbar">
      <TableUtility configuration={toolbarConfiguration} data={[]} />
    </div>
  );
});

ResourceLoadingDetailsToolbar.displayName = 'ResourceLoadingDetailsToolbar';

const TableUtilitySkeleton = React.memo(({
  scrollHeight = '455px',
  loaderSkeleton = {},
  size = 'small',
}) => {
  const {
    rows = 4,
    bodyCells = 1,
    columns = 5,
  } = loaderSkeleton;

  const skeletonRows = useMemo(
    () => Array.from({ length: rows }, (_, index) => ({
      id: `skeleton-row-${index + 1}`,
    })),
    [rows],
  );

  const skeletonColumns = useMemo(
    () => Array.from({ length: columns }, (_, index) => index),
    [columns],
  );

  const skeletonBodyCells = useMemo(
    () => Array.from({ length: bodyCells }, (_, index) => index),
    [bodyCells],
  );

  return (
    <div className="card">
      <DataTable
        size={size}
        value={skeletonRows}
        className="p-datatable-striped"
        scrollable
        scrollHeight={scrollHeight}
      >
        {skeletonColumns.map((columnIndex) => (
          <Column
            key={`skeleton-column-${columnIndex + 1}`}
            field=""
            header=""
            style={{ width: `${100 / Math.max(columns, 1)}%` }}
            body={() => (
              <div>
                {skeletonBodyCells.map((cellIndex) => (
                  <Skeleton
                    key={`skeleton-cell-${columnIndex + 1}-${cellIndex + 1}`}
                    className="marginHorizon"
                  />
                ))}
              </div>
            )}
          />
        ))}
      </DataTable>
    </div>
  );
});

TableUtilitySkeleton.displayName = 'TableUtilitySkeleton';

TableUtilitySkeleton.propTypes = {
  scrollHeight: PropTypes.string,
  loaderSkeleton: PropTypes.shape({
    rows: PropTypes.number,
    bodyCells: PropTypes.number,
    columns: PropTypes.number,
  }),
  size: PropTypes.string,
};

const SummaryTable = React.memo(({
  table,
  tableScrollHeight = '455px',
  loading = false,
}) => {
  const columns = table.tableConfiguration || [];
  const rows = table.tableData || [];
  const isTableLoading = loading || columns.length === 0;
  const processedRows = useMemo(() => rows.map((row) => {
    const nextRow = { ...row };

    columns.forEach((column) => {
      const field = column.field;
      const value = row[field];

      if (value === null || value === undefined || value === '') {
        return;
      }

      let hasFormattedElement = false;
      if (!row.isTotalRow && field === 'deviation') {
        const num = Number(value);
        if (num > 0) {
          nextRow[field] = <span className="is-red">{formatValue(value, column.fractionDigits)}</span>;
          hasFormattedElement = true;
        } else if (num < 0) {
          nextRow[field] = <span className="is-green">{formatValue(value, column.fractionDigits)}</span>;
          hasFormattedElement = true;
        }
      }

      if (!row.isTotalRow && field === 'rlPlanEstimate' && row.withinDeviation !== undefined) {
        nextRow[field] = (
          <span className={row.withinDeviation ? 'is-green' : 'is-red'}>
            {formatValue(value, column.fractionDigits)}
          </span>
        );
        hasFormattedElement = true;
      }

      const shouldFormatNumber = column.fractionDigits !== undefined
        || column.align === 'right'
        || column.headerAlign === 'right';

      if (!hasFormattedElement && shouldFormatNumber) {
        nextRow[field] = formatValue(value, column.fractionDigits);
      }
    });

    return nextRow;
  }), [columns, rows]);

  const toolbarConfiguration = useMemo(
    () => buildToolbarOnlyConfiguration({
      heading: table.title,
    }),
    [table.title],
  );

  const tableConfiguration = useMemo(
    () => buildReadOnlyTableConfiguration(
      columns.map((column) => ({
        ...column,
        field: column.field,
        header: column.header,
        ...(column.align ? { align: column.align, headerAlign: column.align } : {}),
        ...(column.headerClassName || column.className || column.align === 'right'
          ? {
            headerClassName: [
              column.headerClassName,
              column.className,
              column.align === 'right' ? 'align-right' : '',
            ].filter(Boolean).join(' '),
          }
          : {}),
        ...(column.bodyClassName || column.className || column.align === 'right'
          ? {
            bodyClassName: [
              column.bodyClassName,
              column.className,
              column.align === 'right' ? 'align-right' : '',
            ].filter(Boolean).join(' '),
          }
          : {}),
        body: (row) => {
          const cellValue = row[column.field];

          if (React.isValidElement(cellValue)) {
            return (
              <span
                className={[
                  column.align === 'right' ? 'align-right' : '',
                  column.bodyClassName,
                  column.className,
                  row.isTotalRow ? 'resource-loading-total-cell' : '',
                ].filter(Boolean).join(' ')}
              >
                {cellValue}
              </span>
            );
          }

          let colorClass = '';
          if (!row.isTotalRow) {
            if (column.field === 'deviation') {
              const val = Number(cellValue || 0);
              if (val > 0) {
                colorClass = 'is-red';
              } else if (val < 0) {
                colorClass = 'is-green';
              }
            } else if (column.field === 'rlPlanEstimate') {
              if (row.withinDeviation !== undefined) {
                colorClass = row.withinDeviation ? 'is-green' : 'is-red';
              }
            }
          }

          return (
            <span
              className={[
                column.align === 'right' ? 'align-right' : '',
                column.bodyClassName,
                column.className,
                row.isTotalRow ? 'resource-loading-total-cell' : '',
                colorClass,
              ].filter(Boolean).join(' ')}
            >
              {isBlankExportValue(cellValue) ? '' : formatValue(cellValue, column.fractionDigits)}
            </span>
          );
        },
      })),
      {
        tableScrollHeight,
      },
    ),
    [columns, tableScrollHeight],
  );

  return (
    <section className="resource-loading-plan-section">
      <div className="resource-loading-plan-table-utility resource-loading-plan-table-utility--section-heading">
        <TableUtility configuration={toolbarConfiguration} data={[]} />
      </div>
      <div className="resource-loading-plan-table-utility resource-loading-plan-table-utility--summary">
        {isTableLoading ? (
          <TableUtilitySkeleton
            scrollHeight={tableScrollHeight}
            size={tableConfiguration?.tableSettings?.size || 'small'}
            loaderSkeleton={{
              ...(tableConfiguration?.tableSettings?.loaderSkeleton || {}),
              columns: Math.min(
                Math.max(columns.length || RESOURCE_LOADING_SKELETON_COLUMN_LIMIT, 1),
                RESOURCE_LOADING_SKELETON_COLUMN_LIMIT,
              ),
            }}
          />
        ) : (
          <TableUtility configuration={tableConfiguration} data={processedRows} />
        )}
      </div>
    </section>
  );
});

SummaryTable.displayName = 'SummaryTable';

const ResourceLoadingDetailDeleteConfirm = ({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="resource-loading-delete-backdrop"
      onClick={(event) => event.target === event.currentTarget && onClose()}
      role="presentation"
    >
      <div className="resource-loading-delete-modal">
        <div className="resource-loading-delete-modal__header">
          <h3 className="resource-loading-delete-modal__title">{title}</h3>
        </div>
        <div className="resource-loading-delete-modal__body">
          <div className="resource-loading-delete-modal__warning" aria-hidden="true">
            <span className="resource-loading-delete-modal__warning-icon" />
          </div>
          <div className="resource-loading-delete-modal__message">{message}</div>
        </div>
        <div className="resource-loading-delete-modal__footer">
          <button
            type="button"
            className="resource-loading-delete-modal__btn resource-loading-delete-modal__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="resource-loading-delete-modal__btn resource-loading-delete-modal__btn--danger"
            onClick={onConfirm}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

ResourceLoadingDetailDeleteConfirm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

const ResourceLoadingAllocationTable = React.memo(({
  rows,
  editingAllocationIndex,
  canManageAllocations,
  canDeleteAllocations,
  onEditAllocation,
  onDeleteAllocation,
}) => (
  <table className="resource-loading-detail-modal__allocation-table">
    <thead>
      <tr>
        <th>No. of Resources</th>
        <th>Allocation %</th>
        <th>From Date</th>
        <th>To Date</th>
        <th className="resource-loading-detail-modal__action-header">Action</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((allocationRow, allocationIndex) => (
        <tr
          key={allocationRow.rowKey || `${allocationRow.fromDate || 'row'}-${allocationIndex}`}
          className={allocationRow.placeholderRow
            ? 'resource-loading-detail-modal__placeholder-row'
            : ''}
        >
          <td>{allocationRow.placeholderRow ? '' : (allocationRow.headCount || '-')}</td>
          <td>
            {allocationRow.placeholderRow
              ? ''
              : (allocationRow.allocationPercent ? `${allocationRow.allocationPercent}%` : '-')}
          </td>
          <td>{allocationRow.placeholderRow ? '' : formatDateForDisplay(allocationRow.fromDate)}</td>
          <td>{allocationRow.placeholderRow ? '' : formatDateForDisplay(allocationRow.toDate)}</td>
          <td className="resource-loading-detail-modal__action-cell">
            {allocationRow.placeholderRow ? null : (
              editingAllocationIndex === allocationIndex ? (
                <span className="resource-loading-detail-modal__editing-tag">
                  Editing...
                </span>
              ) : (
                <div className="resource-loading-detail-modal__row-actions">
                  {canManageAllocations ? (
                  <button
                    type="button"
                    className="resource-loading-detail-modal__icon-btn"
                    aria-label={`Edit allocation row ${allocationIndex + 1}`}
                    title="Edit"
                    onClick={() => onEditAllocation(allocationRow, allocationIndex)}
                  >
                      <img src={editActionIcon} alt="Edit" />
                  </button>
                ) : null}
                {canDeleteAllocations ? (
                  <button
                    type="button"
                    className="resource-loading-detail-modal__icon-btn"
                    aria-label={`Delete allocation row ${allocationIndex + 1}`}
                    title="Delete"
                    onClick={() => onDeleteAllocation(allocationIndex)}
                  >
                      <img src={deleteActionIcon} alt="Delete" />
                  </button>
                ) : null}
                </div>
              )
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
));

ResourceLoadingAllocationTable.displayName = 'ResourceLoadingAllocationTable';

ResourceLoadingAllocationTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  editingAllocationIndex: PropTypes.number,
  canManageAllocations: PropTypes.bool.isRequired,
  canDeleteAllocations: PropTypes.bool.isRequired,
  onEditAllocation: PropTypes.func.isRequired,
  onDeleteAllocation: PropTypes.func.isRequired,
};

ResourceLoadingAllocationTable.defaultProps = {
  editingAllocationIndex: null,
};

const ResourceLoadingDetailModal = ({
  isOpen,
  mode,
  initialRow,
  canManageAllocations,
  canDeleteAllocations,
  roleOptions,
  workExperienceOptions,
  roleExperienceMappings,
  locationOptions,
  projectStartDate,
  projectEndDate,
  onClose,
  onSubmit,
  onValidationMessage,
}) => {
  const [form, setForm] = useState(EMPTY_RESOURCE_LOADING_DETAIL_FORM);
  const [allocations, setAllocations] = useState([]);
  const [allocationDraft, setAllocationDraft] = useState(createEmptyAllocationRow());
  const [allocationErrors, setAllocationErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [editingAllocationIndex, setEditingAllocationIndex] = useState(null);
  const [allocationDeleteIndex, setAllocationDeleteIndex] = useState(null);
  const isProfileFrozen = mode === 'edit' || allocations.length > 0;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const nextState = toResourceLoadingDetailFormState(initialRow);
    setForm(nextState.form);
    setAllocations(nextState.allocations || []);
    setAllocationDraft(createEmptyAllocationRow());
    setAllocationErrors({});
    setFormErrors({});
    setEditingAllocationIndex(null);
    setAllocationDeleteIndex(null);
  }, [initialRow, isOpen]);

  const handleFormChange = useCallback((field, value) => {
    const isLockedProfileField = ['role', 'workExperience', 'location', 'skillsComments'].includes(field);
    if (isProfileFrozen && isLockedProfileField) {
      return;
    }

    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === 'role' ? { workExperience: '' } : {}),
    }));

    if (formErrors[field] || (field === 'role' && formErrors.workExperience)) {
      setFormErrors((current) => ({
        ...current,
        [field]: '',
        ...(field === 'role' ? { workExperience: '' } : {}),
      }));
    }
  }, [formErrors, isProfileFrozen]);

  const handleAllocationChange = useCallback((field, value) => {
    const nextValue = field === 'headCount'
      ? sanitizeHeadCountInput(value)
      : field === 'allocationPercent'
        ? sanitizeAllocationPercentInput(value)
        : value;

    setAllocationDraft((current) => ({ ...current, [field]: nextValue }));
    if (allocationErrors[field]) {
      setAllocationErrors((current) => ({ ...current, [field]: '' }));
    }
  }, [allocationErrors]);

  const validateDetailForm = useCallback(() => {
    const nextErrors = {};
    if (!String(form.role || '').trim()) nextErrors.role = 'Role is required';
    if (!String(form.workExperience || '').trim()) nextErrors.workExperience = 'Work experience is required';
    if (!String(form.location || '').trim()) nextErrors.location = 'Location is required';
    return nextErrors;
  }, [form.location, form.role, form.workExperience]);

  const workExperienceOptionsByRole = useMemo(() => {
    const optionsByExperienceId = new Map(
      workExperienceOptions.map((option) => [String(option.value), option]),
    );
    const optionsByRole = new Map();
    const mappedExperienceIdsByRole = new Map();

    (roleExperienceMappings || []).forEach((mapping) => {
      const roleId = String(mapping?.roleId || '');
      const experienceId = String(mapping?.experienceId || '');
      const option = optionsByExperienceId.get(experienceId);

      if (!roleId || !experienceId || !option) {
        return;
      }

      if (!optionsByRole.has(roleId)) {
        optionsByRole.set(roleId, []);
        mappedExperienceIdsByRole.set(roleId, new Set());
      }

      if (mappedExperienceIdsByRole.get(roleId).has(experienceId)) {
        return;
      }

      mappedExperienceIdsByRole.get(roleId).add(experienceId);
      optionsByRole.get(roleId).push(option);
    });

    optionsByRole.forEach((options, roleId) => {
      optionsByRole.set(roleId, sortExperienceOptions(options));
    });

    return optionsByRole;
  }, [roleExperienceMappings, workExperienceOptions]);

  const filteredWorkExperienceOptions = useMemo(() => {
    if (!form.role) {
      return [];
    }

    return workExperienceOptionsByRole.get(String(form.role)) || [];
  }, [form.role, workExperienceOptionsByRole]);

  const validateAllocationDraft = useCallback(() => {
    const nextErrors = {};
    if (!allocationDraft.fromDate) nextErrors.fromDate = 'From date is required';
    if (!allocationDraft.toDate) nextErrors.toDate = 'To date is required';
    if (projectStartDate && allocationDraft.fromDate && allocationDraft.fromDate < projectStartDate) {
      nextErrors.fromDate = 'From date cannot be before project start date';
    }
    if (projectEndDate && allocationDraft.fromDate && allocationDraft.fromDate > projectEndDate) {
      nextErrors.fromDate = 'From date cannot be after project end date';
    }
    if (projectStartDate && allocationDraft.toDate && allocationDraft.toDate < projectStartDate) {
      nextErrors.toDate = 'To date cannot be before project start date';
    }
    if (projectEndDate && allocationDraft.toDate && allocationDraft.toDate > projectEndDate) {
      nextErrors.toDate = 'To date cannot be after project end date';
    }
    if (allocationDraft.fromDate && allocationDraft.toDate && allocationDraft.fromDate > allocationDraft.toDate) {
      nextErrors.toDate = 'To date must be on or after From date';
    }
    const shouldValidateOverlap = allocationDraft.fromDate
      && allocationDraft.toDate
      && allocationDraft.fromDate <= allocationDraft.toDate;
    const overlappingAllocation = shouldValidateOverlap
      ? findOverlappingAllocationRange({
        allocations,
        fromDate: allocationDraft.fromDate,
        toDate: allocationDraft.toDate,
        ignoredIndex: editingAllocationIndex,
      })
      : null;
    if (overlappingAllocation) {
      Object.assign(nextErrors, getAllocationOverlapFieldErrors(overlappingAllocation));
      nextErrors.dateRange = buildAllocationOverlapMessage(overlappingAllocation);
    }
    if (!allocationDraft.headCount && allocationDraft.headCount !== 0) {
      nextErrors.headCount = 'Head count is required';
    } else if (!Number.isInteger(Number(allocationDraft.headCount))) {
      nextErrors.headCount = 'Head count must be a whole number';
    } else if (Number(allocationDraft.headCount) <= 0) {
      nextErrors.headCount = 'Head count must be greater than zero';
    }
    if (!allocationDraft.allocationPercent && allocationDraft.allocationPercent !== 0) {
      nextErrors.allocationPercent = 'Allocation % is required';
    } else if (Number(allocationDraft.allocationPercent) <= 0) {
      nextErrors.allocationPercent = 'Allocation % must be greater than zero';
    } else if (Number(allocationDraft.allocationPercent) > 100) {
      nextErrors.allocationPercent = 'Allocation % must be less than or equal to 100';
    }
    return nextErrors;
  }, [
    allocationDraft.allocationPercent,
    allocationDraft.fromDate,
    allocationDraft.headCount,
    allocationDraft.toDate,
    allocations,
    editingAllocationIndex,
    projectEndDate,
    projectStartDate,
  ]);

  const handleAddOrUpdateAllocation = useCallback(() => {
    const nextFormErrors = validateDetailForm();
    const nextAllocationErrors = validateAllocationDraft();
    if (Object.keys(nextFormErrors).length || Object.keys(nextAllocationErrors).length) {
      setFormErrors(nextFormErrors);
      setAllocationErrors(nextAllocationErrors);
      if (nextAllocationErrors.dateRange) {
        onValidationMessage(nextAllocationErrors.dateRange, 'error');
      }
      return;
    }

    const nextAllocation = {
      id: allocationDraft.id ?? null,
      rowKey: allocationDraft.rowKey || `allocation-${Date.now()}-${allocations.length + 1}`,
      role: form.role,
      workExperience: form.workExperience,
      location: form.location,
      skillsComments: form.skillsComments,
      fromDate: normalizeDateValue(allocationDraft.fromDate),
      toDate: normalizeDateValue(allocationDraft.toDate),
      headCount: String(allocationDraft.headCount),
      allocationPercent: String(allocationDraft.allocationPercent),
      numResources: toEffectiveResourceCount({
        headCount: allocationDraft.headCount,
        allocationPercent: allocationDraft.allocationPercent,
      }),
    };

    setAllocations((current) => {
      const nextAllocations = editingAllocationIndex !== null
        ? current.map((allocationRow, allocationIndex) => (
          allocationIndex === editingAllocationIndex ? nextAllocation : allocationRow
        ))
        : [...current, nextAllocation];

      return mergeResourceLoadingAllocationsByDateRange(nextAllocations);
    });

    setAllocationDraft(createEmptyAllocationRow());
    setEditingAllocationIndex(null);
    setAllocationErrors({});
    setFormErrors({});
  }, [
    allocationDraft.allocationPercent,
    allocationDraft.fromDate,
    allocationDraft.headCount,
    allocationDraft.toDate,
    allocations.length,
    editingAllocationIndex,
    form.location,
    form.role,
    form.skillsComments,
    form.workExperience,
    validateAllocationDraft,
    validateDetailForm,
    onValidationMessage,
  ]);

  const handleEditAllocation = useCallback((allocationRow, allocationIndex) => {
    setAllocationDraft({
      fromDate: allocationRow.fromDate || '',
      toDate: allocationRow.toDate || '',
      headCount: allocationRow.headCount || '',
      allocationPercent: allocationRow.allocationPercent || '',
      id: allocationRow.id ?? null,
      rowKey: allocationRow.rowKey || '',
    });
    setEditingAllocationIndex(allocationIndex);
    setAllocationErrors({});
  }, []);

  const handleCancelAllocationEdit = useCallback(() => {
    setAllocationDraft(createEmptyAllocationRow());
    setEditingAllocationIndex(null);
    setAllocationErrors({});
  }, []);

  const handleOpenDeleteAllocation = useCallback((allocationIndex) => {
    setAllocationDeleteIndex(allocationIndex);
  }, []);

  const handleCloseDeleteAllocation = useCallback(() => {
    setAllocationDeleteIndex(null);
  }, []);

  const handleConfirmDeleteAllocation = useCallback(() => {
    if (allocationDeleteIndex === null) {
      return;
    }

    setAllocations((current) => (
      current.filter((_, currentIndex) => currentIndex !== allocationDeleteIndex)
    ));
    setAllocationErrors({});
    if (editingAllocationIndex === allocationDeleteIndex) {
      setAllocationDraft(createEmptyAllocationRow());
      setEditingAllocationIndex(null);
    } else if (
      editingAllocationIndex !== null
      && editingAllocationIndex > allocationDeleteIndex
    ) {
      setEditingAllocationIndex((current) => current - 1);
    }
    setAllocationDeleteIndex(null);
  }, [allocationDeleteIndex, editingAllocationIndex]);

  const handleSave = useCallback(() => {
    if (allocations.length > 0) {
      onSubmit({ form, allocations });
      return;
    }

    const nextErrors = validateDetailForm();
    const nextAllocationErrors = validateAllocationDraft();
    if (Object.keys(nextErrors).length || Object.keys(nextAllocationErrors).length) {
      setFormErrors(nextErrors);
      setAllocationErrors(nextAllocationErrors);
      return;
    }

    const finalAllocation = {
      ...allocationDraft,
      role: form.role,
      workExperience: form.workExperience,
      location: form.location,
      skillsComments: form.skillsComments,
      fromDate: normalizeDateValue(allocationDraft.fromDate),
      toDate: normalizeDateValue(allocationDraft.toDate),
      headCount: String(allocationDraft.headCount),
      allocationPercent: String(allocationDraft.allocationPercent),
      numResources: toEffectiveResourceCount({
        headCount: allocationDraft.headCount,
        allocationPercent: allocationDraft.allocationPercent,
      }),
    };

    onSubmit({ form, allocations: [finalAllocation] });
  }, [allocationDraft, allocations, form, onSubmit, validateAllocationDraft, validateDetailForm]);

  const saveButtonLabel = mode === 'edit' ? 'Update' : 'Save';
  const allocationButtonLabel = editingAllocationIndex !== null ? 'Update' : 'Add';
  const visibleAllocationRows = useMemo(() => (
    allocations.length >= RESOURCE_LOADING_DETAIL_MIN_VISIBLE_ROWS
      ? allocations
      : [
        ...allocations,
        ...Array.from({
          length: RESOURCE_LOADING_DETAIL_MIN_VISIBLE_ROWS - allocations.length,
        }, (_, index) => ({
          placeholderRow: true,
          rowKey: `placeholder-${index + 1}`,
        })),
      ]
  ), [allocations]);
  const hasScrollableAllocations = allocations.length > RESOURCE_LOADING_DETAIL_MIN_VISIBLE_ROWS;

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="resource-loading-detail-modal-backdrop"
      onClick={(event) => event.target === event.currentTarget && onClose()}
      role="presentation"
    >
      <div className="resource-loading-detail-modal">
        <div className="resource-loading-detail-modal__header">
          <h3 className="resource-loading-detail-modal__title">
            {mode === 'edit' ? 'Edit Resource Loading Details' : 'Add Resource Loading Details'}
          </h3>
        </div>

        <div className="resource-loading-detail-modal__body">
          <div className="resource-loading-detail-modal__form-grid">
            <div className="resource-loading-detail-modal__field">
              <label className={`resource-loading-detail-modal__label${formErrors.role ? ' is-error' : ''}`}>
                Role<span className="resource-loading-detail-modal__required">*</span>
              </label>
              <select
                className={[
                  'resource-loading-detail-modal__select',
                  form.role ? '' : 'is-placeholder',
                  formErrors.role ? 'is-error' : '',
                ].filter(Boolean).join(' ')}
                value={form.role}
                onChange={(event) => handleFormChange('role', event.target.value)}
                disabled={isProfileFrozen}
              >
                <option value="">Select</option>
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="resource-loading-detail-modal__field">
              <label className={`resource-loading-detail-modal__label${formErrors.workExperience ? ' is-error' : ''}`}>
                Work Experience<span className="resource-loading-detail-modal__required">*</span>
              </label>
              <select
                className={[
                  'resource-loading-detail-modal__select',
                  form.workExperience ? '' : 'is-placeholder',
                  formErrors.workExperience ? 'is-error' : '',
                ].filter(Boolean).join(' ')}
                value={form.workExperience}
                onChange={(event) => handleFormChange('workExperience', event.target.value)}
                disabled={!form.role || isProfileFrozen}
              >
                <option value="">Select</option>
                {filteredWorkExperienceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="resource-loading-detail-modal__field">
              <label className={`resource-loading-detail-modal__label${formErrors.location ? ' is-error' : ''}`}>
                Location<span className="resource-loading-detail-modal__required">*</span>
              </label>
              <select
                className={[
                  'resource-loading-detail-modal__select',
                  form.location ? '' : 'is-placeholder',
                  formErrors.location ? 'is-error' : '',
                ].filter(Boolean).join(' ')}
                value={form.location}
                onChange={(event) => handleFormChange('location', event.target.value)}
                disabled={isProfileFrozen}
              >
                <option value="">Select</option>
                {locationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="resource-loading-detail-modal__field">
              <label className="resource-loading-detail-modal__label">Skills / Comments</label>
              <input
                type="text"
                className="resource-loading-detail-modal__input"
                value={form.skillsComments}
                placeholder="Enter..."
                onChange={(event) => handleFormChange('skillsComments', event.target.value)}
                disabled={isProfileFrozen}
              />
            </div>
          </div>

          <div className="resource-loading-detail-modal__allocation-section">
            <div className="resource-loading-detail-modal__allocation-grid">
              <div className="resource-loading-detail-modal__field">
                <label className={`resource-loading-detail-modal__label${allocationErrors.headCount ? ' is-error' : ''}`}>
                  No. of Resources (Head Count)<span className="resource-loading-detail-modal__required">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`resource-loading-detail-modal__input${allocationErrors.headCount ? ' is-error' : ''}`}
                  value={allocationDraft.headCount}
                  placeholder="Enter"
                  onChange={(event) => handleAllocationChange('headCount', event.target.value)}
                />
              </div>
              <div className="resource-loading-detail-modal__field">
                <label className={`resource-loading-detail-modal__label${allocationErrors.allocationPercent ? ' is-error' : ''}`}>
                  Allocation %<span className="resource-loading-detail-modal__required">*</span>
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`resource-loading-detail-modal__input${allocationErrors.allocationPercent ? ' is-error' : ''}`}
                  value={allocationDraft.allocationPercent}
                  placeholder="Enter"
                  onChange={(event) => handleAllocationChange('allocationPercent', event.target.value)}
                />
              </div>
              <div className="resource-loading-detail-modal__field resource-loading-detail-modal__field--date">
                <label className={`resource-loading-detail-modal__label${allocationErrors.fromDate ? ' is-error' : ''}`}>
                  From Date<span className="resource-loading-detail-modal__required">*</span>
                </label>
                <ResourceLoadingModalDateInput
                  ariaLabel="Allocation from date"
                  value={allocationDraft.fromDate}
                  placeholder="dd-mm-yyyy"
                  minDate={projectStartDate || null}
                  maxDate={projectEndDate || null}
                  referenceDate={projectStartDate || null}
                  hasError={Boolean(allocationErrors.fromDate)}
                  onChange={(nextDate) => handleAllocationChange('fromDate', nextDate)}
                />
              </div>
              <div className="resource-loading-detail-modal__field resource-loading-detail-modal__field--date">
                <label className={`resource-loading-detail-modal__label${allocationErrors.toDate ? ' is-error' : ''}`}>
                  To Date<span className="resource-loading-detail-modal__required">*</span>
                </label>
                <ResourceLoadingModalDateInput
                  ariaLabel="Allocation to date"
                  value={allocationDraft.toDate}
                  placeholder="dd-mm-yyyy"
                  minDate={projectStartDate || null}
                  maxDate={projectEndDate || null}
                  referenceDate={projectEndDate || null}
                  hasError={Boolean(allocationErrors.toDate)}
                  onChange={(nextDate) => handleAllocationChange('toDate', nextDate)}
                />
              </div>
            </div>

            <div className="resource-loading-detail-modal__allocation-actions">
              {editingAllocationIndex !== null ? (
                <button
                  type="button"
                  className="resource-loading-detail-modal__btn resource-loading-detail-modal__btn--ghost"
                  onClick={handleCancelAllocationEdit}
                >
                  Cancel
                </button>
              ) : null}
              <button
                type="button"
                className="resource-loading-detail-modal__btn resource-loading-detail-modal__btn--primary"
                onClick={handleAddOrUpdateAllocation}
              >
                {allocationButtonLabel}
              </button>
            </div>

            <div
              className={[
                'resource-loading-detail-modal__allocation-table-wrap',
                hasScrollableAllocations
                  ? 'resource-loading-detail-modal__allocation-table-wrap--scrollable'
                  : '',
              ].filter(Boolean).join(' ')}
            >
              <ResourceLoadingAllocationTable
                rows={visibleAllocationRows}
                editingAllocationIndex={editingAllocationIndex}
                canManageAllocations={canManageAllocations}
                canDeleteAllocations={canDeleteAllocations}
                onEditAllocation={handleEditAllocation}
                onDeleteAllocation={handleOpenDeleteAllocation}
              />
            </div>
          </div>
        </div>

        <div className="resource-loading-detail-modal__footer">
          <button
            type="button"
            className="resource-loading-detail-modal__btn resource-loading-detail-modal__btn--ghost"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="resource-loading-detail-modal__btn resource-loading-detail-modal__btn--primary"
            onClick={handleSave}
          >
            {saveButtonLabel}
          </button>
        </div>
      </div>
      <ResourceLoadingDetailDeleteConfirm
        isOpen={allocationDeleteIndex !== null}
        title="Delete Request"
        message="Are you sure you want to delete this allocation?"
        onClose={handleCloseDeleteAllocation}
        onConfirm={handleConfirmDeleteAllocation}
      />
    </div>
  );
};

ResourceLoadingDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  initialRow: PropTypes.shape({}),
  canManageAllocations: PropTypes.bool.isRequired,
  canDeleteAllocations: PropTypes.bool.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  workExperienceOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  roleExperienceMappings: PropTypes.arrayOf(PropTypes.shape({
    roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    experienceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })).isRequired,
  locationOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })).isRequired,
  projectStartDate: PropTypes.string,
  projectEndDate: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onValidationMessage: PropTypes.func,
};

ResourceLoadingDetailModal.defaultProps = {
  initialRow: null,
  projectStartDate: '',
  projectEndDate: '',
  onValidationMessage: () => {},
};

ToolbarDateInput.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  disabled: PropTypes.bool,
};

ToolbarDateInput.defaultProps = {
  minDate: null,
  maxDate: null,
  disabled: false,
};

ResourceLoadingPlanToolbarControls.propTypes = {
  fromDate: PropTypes.string.isRequired,
  toDate: PropTypes.string.isRequired,
  onChangeFromDate: PropTypes.func.isRequired,
  onChangeToDate: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  fromMinDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  fromMaxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  toMinDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  toMaxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  dateDisabled: PropTypes.bool,
  applyDisabled: PropTypes.bool,
  exportElement: PropTypes.node,
};

ResourceLoadingPlanToolbarControls.defaultProps = {
  fromMinDate: null,
  fromMaxDate: null,
  toMinDate: null,
  toMaxDate: null,
  dateDisabled: false,
  applyDisabled: false,
  exportElement: null,
};

ResourceLoadingPlanMultiExportButton.propTypes = {
  tables: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

ResourceLoadingDetailsToolbar.propTypes = {
  canViewApprovalHistory: PropTypes.bool.isRequired,
  canAddResourceLoadingDetail: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool,
  onOpenApprovalHistory: PropTypes.func.isRequired,
  onAddResourceLoadingDetail: PropTypes.func.isRequired,
};

SummaryTable.propTypes = {
  table: PropTypes.shape({
    title: PropTypes.string,
    tableConfiguration: PropTypes.arrayOf(PropTypes.shape({})),
    tableData: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  tableScrollHeight: PropTypes.string,
  loading: PropTypes.bool,
};

SummaryTable.defaultProps = {
  tableScrollHeight: '455px',
  loading: false,
};

const buildToolbarElements = ({
  fromDate,
  toDate,
  onChangeFromDate,
  onChangeToDate,
  onApply,
  fromMaxDate = null,
  toMinDate = null,
  dateDisabled = false,
  applyDisabled = false,
  exportElement = null,
}) => ([
  <ResourceLoadingPlanToolbarControls
    key="resource-loading-toolbar-controls"
    fromDate={fromDate}
    toDate={toDate}
    onChangeFromDate={onChangeFromDate}
    onChangeToDate={onChangeToDate}
    onApply={onApply}
    fromMinDate={null}
    fromMaxDate={applyDisabled ? fromDate || fromMaxDate || null : fromMaxDate || toDate || null}
    toMinDate={applyDisabled ? toDate || toMinDate || null : toMinDate || fromDate || null}
    toMaxDate={null}
    dateDisabled={dateDisabled}
    applyDisabled={applyDisabled}
    exportElement={exportElement}
  />,
]);

const buildResourceLoadingDetailActionCell = (
  row = {},
  onEdit,
  onDelete,
  canEdit = false,
  canDelete = false,
) => {
  if (!row || row.isTotalRow) {
    return null;
  }

  return (
    <div
      className="resource-loading-action-cell"
      role="presentation"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="resource-loading-action-cell__button resource-loading-action-cell__button--edit"
        disabled={!canEdit}
        onClick={(event) => {
          event.stopPropagation();
          if (!canEdit || !onEdit) {
            return;
          }
          onEdit(row);
        }}
        aria-label="Edit resource loading detail"
        title="Edit"
      >
        <img
          src={editActionIcon}
          alt="Edit"
          className="resource-loading-action-cell__icon resource-loading-action-cell__icon--edit"
        />
      </button>
      <button
        type="button"
        className="resource-loading-action-cell__button resource-loading-action-cell__button--delete"
        disabled={!canDelete}
        onClick={(event) => {
          event.stopPropagation();
          if (!canDelete || !onDelete) {
            return;
          }
          onDelete(row);
        }}
        aria-label="Delete resource loading detail"
        title="Delete"
      >
        <img
          src={deleteActionIcon}
          alt="Delete"
          className="resource-loading-action-cell__icon resource-loading-action-cell__icon--delete"
        />
      </button>
    </div>
  );
};

const __buildResourceLoadingAllocationBreakdownCell = (row = {}) => {
  if (!row || row.isTotalRow) {
    return null;
  }

  const breakdownRows = getResourceLoadingAllocationBreakdownRows(row);
  if (breakdownRows.length === 0) {
    return '-';
  }

  const visibleRows = breakdownRows.slice(0, 1);
  const hasHiddenAllocations = breakdownRows.length > visibleRows.length;
  const tooltipText = breakdownRows
    .map((allocation) => (
      `${allocation.headCount || '-'} - ${formatAllocationPercentForDisplay(allocation.allocationPercent)} - ${formatDateForDisplay(allocation.fromDate)} - ${formatDateForDisplay(allocation.toDate)}`
    ))
    .join('\n');

  return (
    <div
      className="resource-loading-allocation-breakdown"
      role="presentation"
      title={tooltipText}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="resource-loading-allocation-breakdown__list">
        {visibleRows.map((allocation, allocationIndex) => (
          <div
            className="resource-loading-allocation-breakdown__item"
            title={tooltipText}
            key={`${allocation.fromDate}-${allocation.toDate}-${allocationIndex}`}
          >
            {`${allocation.headCount || '-'} - ${formatAllocationPercentForDisplay(allocation.allocationPercent)} - ${formatDateForDisplay(allocation.fromDate)} - ${formatDateForDisplay(allocation.toDate)}${hasHiddenAllocations ? ' ...' : ''}`}
          </div>
        ))}
      </div>
    </div>
  );
};

const ResourceLoadingPlanBody = React.memo(({
  initialEstimationTable,
  idealCategoryTable,
  detailsTableRows,
  detailsTableConfiguration,
  isLoading,
  permissions,
  handleOpenApprovalHistory,
  handleAddResourceLoadingDetail,
}) => {
  const detailsColumns = detailsTableConfiguration?.tableSettings?.tableConfig || [];
  const isDetailsTableLoading = isLoading || detailsColumns.length === 0;

  return (
    <>
      <SummaryTable
        table={initialEstimationTable}
        tableScrollHeight="455px"
        loading={isLoading}
      />

      <SummaryTable
        table={idealCategoryTable}
        tableScrollHeight="455px"
        loading={isLoading}
      />

      <section className="resource-loading-plan-section">
        <ResourceLoadingDetailsToolbar
          canViewApprovalHistory={Boolean(permissions.canViewApprovalHistory)}
          canAddResourceLoadingDetail={Boolean(permissions.baseCanCreate ?? permissions.canCreate)}
          isLocked={Boolean(permissions.isLocked)}
          onOpenApprovalHistory={handleOpenApprovalHistory}
          onAddResourceLoadingDetail={handleAddResourceLoadingDetail}
        />

        <div
          className={[
            'resource-loading-plan-table-utility',
            'resource-loading-plan-table-utility--details',
            (permissions.baseCanCreate || permissions.baseCanUpdate || permissions.baseCanDelete)
              ? 'resource-loading-plan-table-utility--details-editable'
              : '',
          ].filter(Boolean).join(' ')}
        >
          {isDetailsTableLoading ? (
            <TableUtilitySkeleton
              scrollHeight={detailsTableConfiguration?.tableSettings?.tableScrollHeight || '520px'}
              size={detailsTableConfiguration?.tableSettings?.size || 'small'}
              loaderSkeleton={{
                ...(detailsTableConfiguration?.tableSettings?.loaderSkeleton || {}),
                columns: Math.min(
                  Math.max(detailsColumns.length || RESOURCE_LOADING_SKELETON_COLUMN_LIMIT, 1),
                  RESOURCE_LOADING_SKELETON_COLUMN_LIMIT,
                ),
              }}
            />
          ) : (
            <TableUtility
              configuration={detailsTableConfiguration}
              data={detailsTableRows}
              useCustomBodyRenderer
            />
          )}
        </div>
      </section>
    </>
  );
});

ResourceLoadingPlanBody.displayName = 'ResourceLoadingPlanBody';

ResourceLoadingPlanBody.propTypes = {
  initialEstimationTable: PropTypes.shape({
    title: PropTypes.string,
    tableConfiguration: PropTypes.arrayOf(PropTypes.shape({})),
    tableData: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  idealCategoryTable: PropTypes.shape({
    title: PropTypes.string,
    tableConfiguration: PropTypes.arrayOf(PropTypes.shape({})),
    tableData: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  detailsTableRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  detailsTableConfiguration: PropTypes.shape({
    tableSettings: PropTypes.shape({
      tableScrollHeight: PropTypes.string,
      size: PropTypes.string,
      tableConfig: PropTypes.arrayOf(PropTypes.shape({})),
      loaderSkeleton: PropTypes.shape({
        rows: PropTypes.number,
        bodyCells: PropTypes.number,
        columns: PropTypes.number,
      }),
    }),
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  permissions: PropTypes.shape({
    isLocked: PropTypes.bool,
    baseCanCreate: PropTypes.bool,
    baseCanUpdate: PropTypes.bool,
    baseCanDelete: PropTypes.bool,
    canViewApprovalHistory: PropTypes.bool,
    canCreate: PropTypes.bool,
    canUpdate: PropTypes.bool,
    canDelete: PropTypes.bool,
  }).isRequired,
  handleOpenApprovalHistory: PropTypes.func.isRequired,
  handleAddResourceLoadingDetail: PropTypes.func.isRequired,
};

const ResourceLoadingPlan = () => {
  const location = useLocation();
  const { snackbarOpen } = useSnackbar();
  const { userProfile } = useAuth();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [resourceLoadingPlan, setResourceLoadingPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [resourceLoadingDetailRows, setResourceLoadingDetailRows] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [workExperienceOptions, setWorkExperienceOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [roleExperienceMappings, setRoleExperienceMappings] = useState([]);
  const [detailModalState, setDetailModalState] = useState({
    isOpen: false,
    mode: 'add',
    rowKey: null,
    row: null,
  });
  const [deleteConfirmState, setDeleteConfirmState] = useState({
    isOpen: false,
    rowKey: null,
    row: null,
  });
  const visibleResourceLoadingDetailRows = useMemo(
    () => resourceLoadingDetailRows.filter(isResourceLoadingDataRow),
    [resourceLoadingDetailRows],
  );
  const deferredVisibleResourceLoadingDetailRows = useDeferredValue(visibleResourceLoadingDetailRows);

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const projectId = queryParams.get('projectId');
  const resourceLoadingUserInfo = useMemo(() => {
    const planModuleName = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_PLAN;
    const historyModuleName = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_APPROVAL_HISTORY;
    return {
      emailId: userProfile?.email,
      employeeId: userProfile?.employeeId,
      fullName: userProfile?.user_name,
      permissions: {
        [planModuleName]: userProfile?.permissions?.[planModuleName] || [],
        [historyModuleName]: userProfile?.permissions?.[historyModuleName] || [],
      },
      roleDetails: userProfile?.roleDetails || [],
      superAdmin: Boolean(userProfile?.superAdmin || userProfile?.super_admin),
      globalColors: userProfile?.siteConfiguration?.global_colors,
    };
  }, [
    userProfile?.email,
    userProfile?.employeeId,
    userProfile?.permissions,
    userProfile?.roleDetails,
    userProfile?.siteConfiguration?.global_colors,
    userProfile?.superAdmin,
    userProfile?.super_admin,
    userProfile?.user_name,
  ]);
  const hasResolvedUserPermissions = Boolean(userProfile?.permissions);
  const resourceLoadingUserInfoRef = useRef(resourceLoadingUserInfo);
  const snackbarOpenRef = useRef(snackbarOpen);
  const resourceLoadingPlanCacheRef = useRef(new Map());
  const latestResourceLoadingRequestKeyRef = useRef('');

  useEffect(() => {
    resourceLoadingUserInfoRef.current = resourceLoadingUserInfo;
  }, [resourceLoadingUserInfo]);

  useEffect(() => {
    snackbarOpenRef.current = snackbarOpen;
  }, [snackbarOpen]);

  const applyLoadedResourceLoadingPlan = useCallback((responseData, {
    defer = false,
    preserveDetailRows = false,
  } = {}) => {
    const nextResourceLoadingPlan = {
      ...(responseData?.meta || {}),
      tables: responseData?.data || {},
    };
    const nextFilters = nextResourceLoadingPlan?.filters || {};
    const nextMasters = nextResourceLoadingPlan?.masters || {};
    const nextResourceLoadingDetails = (
      responseData?.data?.resourceLoadingDetails?.tableData
      || responseData?.data?.tables?.resourceLoadingDetails?.tableData
      || nextResourceLoadingPlan?.tables?.resourceLoadingDetails?.tableData
      || []
    );

    const applyNextState = () => {
      setResourceLoadingPlan(nextResourceLoadingPlan);
      if (!preserveDetailRows) {
        setResourceLoadingDetailRows(
          normalizeResourceLoadingDetailRowsWithKeys(nextResourceLoadingDetails)
            .filter((row) => !isResourceLoadingTotalRow(row)),
        );
      }
      setRoleOptions(mapMasterOptions(nextMasters.roles || []));
      setWorkExperienceOptions(mapMasterOptions(nextMasters.workExperiences || []));
      setLocationOptions(mapMasterOptions(nextMasters.locations || []));
      setRoleExperienceMappings(nextMasters.roleExperienceMappings || []);
      setFromDate(nextFilters.fromDate || '');
      setToDate(nextFilters.toDate || '');
    };

    if (defer) {
      startTransition(applyNextState);
      return;
    }

    applyNextState();
  }, []);

  const loadResourceLoadingPlan = useCallback(async ({
    requestedFromDate = '',
    requestedToDate = '',
    preferCache = true,
    showLoader = true,
    preserveDetailRows = false,
  } = {}) => {
    if (!projectId || !hasResolvedUserPermissions) {
      return;
    }

    const cacheKey = [
      projectId,
      requestedFromDate || '',
      requestedToDate || '',
    ].join('::');
    const cachedResponseData = preferCache
      ? resourceLoadingPlanCacheRef.current.get(cacheKey)
      : null;
    const shouldShowLoader = showLoader && !cachedResponseData;

    latestResourceLoadingRequestKeyRef.current = cacheKey;

    if (cachedResponseData) {
      applyLoadedResourceLoadingPlan(cachedResponseData, { preserveDetailRows });
    }

    if (shouldShowLoader) {
      setIsLoading(true);
    }

    try {
      const searchParams = new URLSearchParams();
      searchParams.set('projectId', projectId);

      if (requestedFromDate) {
        searchParams.set('fromDate', requestedFromDate);
      }

      if (requestedToDate) {
        searchParams.set('toDate', requestedToDate);
      }

      const requestUrl = buildProjectServiceApiUrl(RESOURCE_LOADING_BASE_PATH);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: getStoredAuthToken(),
        },
        body: JSON.stringify({
          projectId,
          fromDate: searchParams.get('fromDate') || '',
          toDate: searchParams.get('toDate') || '',
          userInfo: resourceLoadingUserInfoRef.current,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          normalizeErrorMessage(responseData, 'Failed to load resource loading plan.'),
        );
      }

      resourceLoadingPlanCacheRef.current.set(cacheKey, responseData);

      if (latestResourceLoadingRequestKeyRef.current === cacheKey) {
        applyLoadedResourceLoadingPlan(responseData, {
          defer: !shouldShowLoader,
          preserveDetailRows,
        });
      }
    } catch (error) {
      snackbarOpenRef.current(
        error?.message || 'Failed to load resource loading plan.',
        'error',
      );
    } finally {
      if (latestResourceLoadingRequestKeyRef.current === cacheKey) {
        setIsLoading(false);
      }
    }
  }, [
    applyLoadedResourceLoadingPlan,
    hasResolvedUserPermissions,
    projectId,
  ]);

  useEffect(() => {
    if (!projectId) {
      snackbarOpenRef.current('Project id is required to open resource loading plan.', 'error');
      return;
    }

    loadResourceLoadingPlan();
  }, [loadResourceLoadingPlan, projectId]);

  const {
    project = {},
    tables = {},
  } = resourceLoadingPlan || {};
  const userPermissions = userProfile?.permissions || {};
  const isSuperAdmin = Boolean(userProfile?.superAdmin || userProfile?.super_admin);
  const permissions = useMemo(() => {
    const planModule = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_PLAN;
    const historyModule = MODULE_NAMES.DELIVERY_RESOURCE_LOADING_APPROVAL_HISTORY;

    const serverPermissions = resourceLoadingPlan?.permissions || {};
    const hasServerPermissions = Object.keys(serverPermissions).length > 0;
    const getEffectivePermission = (permissionKey, hasLocalAccess) => (
      hasServerPermissions
        ? hasLocalAccess && Boolean(serverPermissions[permissionKey])
        : hasLocalAccess
    );
    const canView = isSuperAdmin || hasPermissionAction(userPermissions, planModule, 'read');
    const canCreate = isSuperAdmin || hasPermissionAction(userPermissions, planModule, 'create');
    const canUpdate = isSuperAdmin || hasPermissionAction(userPermissions, planModule, 'update');
    const canDelete = isSuperAdmin || hasPermissionAction(userPermissions, planModule, 'delete');
    const canViewApprovalHistory = isSuperAdmin
      || hasPermissionAction(userPermissions, historyModule, 'read');
    const canSubmit = canCreate || canUpdate || canDelete;

    return {
      isLocked: Boolean(serverPermissions.isLocked),
      baseCanCreate: getEffectivePermission('baseCanCreate', canCreate),
      baseCanUpdate: getEffectivePermission('baseCanUpdate', canUpdate),
      baseCanDelete: getEffectivePermission('baseCanDelete', canDelete),
      canViewApprovalHistory: getEffectivePermission('canViewApprovalHistory', canViewApprovalHistory),
      canCreate: getEffectivePermission('canCreate', canCreate),
      canUpdate: getEffectivePermission('canUpdate', canUpdate),
      canDelete: getEffectivePermission('canDelete', canDelete),
      canSubmit: getEffectivePermission('canSubmit', canSubmit),
      canView,
    };
  }, [isSuperAdmin, resourceLoadingPlan?.permissions, userPermissions]);
  const initialEstimationTable = tables.initialEstimation || {};
  const idealCategoryTable = tables.idealCategoryRatio || {};
  const detailsTable = tables.resourceLoadingDetails || {};
  const detailWeekFields = useMemo(
    () => getResourceLoadingWeekFields(detailsTable.tableConfiguration || []),
    [detailsTable.tableConfiguration],
  );
  const detailWeekRanges = useMemo(
    () => detailWeekFields.map((field) => ({
      field,
      dates: parseWeekFieldDates(field),
    })),
    [detailWeekFields],
  );
  const roleOptionLabelMap = useMemo(
    () => new Map(roleOptions.map((option) => [String(option.value), option.label])),
    [roleOptions],
  );
  const workExperienceLabelMap = useMemo(
    () => new Map(workExperienceOptions.map((option) => [String(option.value), option.label])),
    [workExperienceOptions],
  );
  const locationLabelMap = useMemo(
    () => new Map(locationOptions.map((option) => [String(option.value), option.label])),
    [locationOptions],
  );
  const resolvedProjectName = project.name || '';
  const projectName = resolvedProjectName || 'Project';

  const handleOpenAddResourceLoadingDetail = useCallback(() => {
    if (!permissions.canCreate) {
      snackbarOpen('You do not have permission to add resource loading details.', 'error');
      return;
    }

    setDetailModalState({
      isOpen: true,
      mode: 'add',
      rowKey: null,
      row: null,
    });
  }, [permissions.canCreate, snackbarOpen]);

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalState({
      isOpen: false,
      mode: 'add',
      rowKey: null,
      row: null,
    });
  }, []);

  const handleOpenEditResourceLoadingDetail = useCallback((row) => {
    if (!permissions.canUpdate || !row || row.isTotalRow) {
      return;
    }

    setDetailModalState({
      isOpen: true,
      mode: 'edit',
      rowKey: row.rowKey || row.id || null,
      row,
    });
  }, [permissions.canUpdate]);

  const handleOpenDeleteResourceLoadingDetail = useCallback((row) => {
    if (!permissions.canDelete || !row || row.isTotalRow) {
      return;
    }

    setDeleteConfirmState({
      isOpen: true,
      rowKey: row.rowKey || row.id || null,
      row,
    });
  }, [permissions.canDelete]);

  const persistResourceLoadingDraft = useCallback(async (rows) => {
    const requestUrl = buildProjectServiceApiUrl(`${RESOURCE_LOADING_BASE_PATH}/draft`);
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        token: getStoredAuthToken(),
      },
      body: JSON.stringify({
        projectId,
        fromDate,
        toDate,
        rows: buildResourceLoadingDraftPayloadRows(rows),
        userInfo: resourceLoadingUserInfoRef.current,
      }),
    });
    const responseData = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        normalizeErrorMessage(responseData, 'Failed to save resource loading details.'),
      );
    }

    return responseData;
  }, [fromDate, projectId, toDate]);

  const handleSubmitResourceLoadingDetail = useCallback(async ({ form, allocations }) => {
    if (detailModalState.mode === 'edit' && !permissions.canUpdate) {
      snackbarOpen('You do not have permission to edit resource loading details.', 'error');
      return;
    }

    if (detailModalState.mode !== 'edit' && !permissions.canCreate) {
      snackbarOpen('You do not have permission to add resource loading details.', 'error');
      return;
    }

    if (!form.role || !form.workExperience || !form.location || allocations.length === 0) {
      snackbarOpen('Please add at least one complete resource loading row before saving.', 'error');
      return;
    }

    const buildRowFromAllocations = (allocationRows) => {
      const normalizedAllocations = mergeResourceLoadingAllocationsByDateRange(allocationRows)
        .map((allocation) => ({
          ...allocation,
          role: form.role,
          workExperience: form.workExperience,
          location: form.location,
          skillsComments: form.skillsComments,
          fromDate: normalizeDateValue(allocation.fromDate),
          toDate: normalizeDateValue(allocation.toDate),
          headCount: String(allocation.headCount),
          allocationPercent: String(allocation.allocationPercent),
          numResources: toEffectiveResourceCount({
            headCount: allocation.headCount,
            allocationPercent: allocation.allocationPercent,
          }),
        }));

      const rowForm = {
        role: form.role,
        workExperience: form.workExperience,
        location: form.location,
        skillsComments: form.skillsComments,
      };

      if (!rowForm.role || !rowForm.workExperience || !rowForm.location || normalizedAllocations.length === 0) {
        return null;
      }

      const selectedRoleLabel = roleOptionLabelMap.get(String(rowForm.role)) || '';
      const selectedWorkExperienceLabel = workExperienceLabelMap.get(String(rowForm.workExperience)) || '';
      const selectedLocationLabel = locationLabelMap.get(String(rowForm.location)) || '';
      let totalEffortValue = 0;
      const weekValues = Object.fromEntries(
        detailWeekRanges.map(({ field, dates }) => {
          let weekEffectiveResources = 0;

          if (dates) {
            normalizedAllocations.forEach((allocation) => {
              const effort = calculateFrontendAllocationEffort({
                fromDate: allocation.fromDate,
                toDate: allocation.toDate,
                numResources: allocation.numResources,
                filterStartDate: dates.fromDate,
                filterEndDate: dates.toDate,
              });

              if (effort > 0) {
                weekEffectiveResources += Number(allocation.numResources || 0);
                totalEffortValue += Number(effort || 0);
              }
            });
          }

          return [field, toRoundedNumber(weekEffectiveResources, 4)];
        }),
      );
      const totalEfforts = Number(totalEffortValue.toFixed(2));
      const firstAllocation = normalizedAllocations[0] || {};

      return {
        ...(detailModalState.mode === 'edit' ? (detailModalState.row || {}) : {}),
        ...rowForm,
        isDeleted: false,
        roleId: rowForm.role ? Number(rowForm.role) : null,
        experienceId: rowForm.workExperience ? Number(rowForm.workExperience) : null,
        locationId: rowForm.location ? Number(rowForm.location) : null,
        role: selectedRoleLabel,
        workExperience: selectedWorkExperienceLabel,
        location: selectedLocationLabel,
        allocations: normalizedAllocations,
        skill: rowForm.skillsComments,
        comments: rowForm.skillsComments,
        fromDate: firstAllocation.fromDate || '',
        toDate: firstAllocation.toDate || '',
        headCount: firstAllocation.headCount || '',
        allocationPercent: firstAllocation.allocationPercent || '',
        numResources: firstAllocation.numResources || 0,
        totalEfforts,
        persistedTotalEfforts: totalEfforts,
        ...weekValues,
        rowKey: detailModalState.mode === 'edit'
          ? (detailModalState.rowKey || detailModalState.row?.rowKey)
          : getResourceLoadingDetailRowKey(rowForm, visibleResourceLoadingDetailRows.length),
      };
    };

    setDetailModalState({
      isOpen: false,
      mode: 'add',
      rowKey: null,
      row: null,
    });

    const updateResourceLoadingRows = () => {
      const nextDetailRow = buildRowFromAllocations(allocations);
      if (!nextDetailRow) {
        snackbarOpen('Please add at least one complete resource loading row before saving.', 'error');
        return;
      }

      const currentDataRows = resourceLoadingDetailRows.filter((row) => !isResourceLoadingTotalRow(row));
      const nextRows = detailModalState.mode === 'edit' && detailModalState.rowKey
        ? currentDataRows.map((row) => (
          row.rowKey === detailModalState.rowKey ? nextDetailRow : row
        ))
        : [...currentDataRows, nextDetailRow];

      startTransition(() => {
        setResourceLoadingDetailRows(nextRows);
      });
    };

    await waitForNextPaint();
    updateResourceLoadingRows();
  }, [
    detailModalState.mode,
    detailModalState.row,
    detailModalState.rowKey,
    detailWeekRanges,
    locationLabelMap,
    permissions.canCreate,
    permissions.canUpdate,
    resourceLoadingDetailRows,
    roleOptionLabelMap,
    snackbarOpen,
    visibleResourceLoadingDetailRows.length,
    workExperienceLabelMap,
  ]);

  const handleConfirmDeleteResourceLoadingDetail = useCallback(async () => {
    if (!permissions.canDelete) {
      snackbarOpen('You do not have permission to delete resource loading details.', 'error');
      return;
    }

    const previousRows = resourceLoadingDetailRows;
    const nextRows = resourceLoadingDetailRows
      .filter((row) => !isResourceLoadingTotalRow(row))
      .filter((row) => row.rowKey !== deleteConfirmState.rowKey);

    setDeleteConfirmState({
      isOpen: false,
      rowKey: null,
      row: null,
    });

    setResourceLoadingDetailRows(nextRows);
    try {
      await persistResourceLoadingDraft(nextRows);
      resourceLoadingPlanCacheRef.current.clear();
      snackbarOpen('Resource loading detail deleted successfully.', 'success');
    } catch (error) {
      setResourceLoadingDetailRows(previousRows);
      snackbarOpen(error?.message || 'Failed to delete resource loading detail.', 'error');
    }
  }, [
    deleteConfirmState.rowKey,
    permissions.canDelete,
    persistResourceLoadingDraft,
    resourceLoadingDetailRows,
    snackbarOpen,
  ]);

  const handleSaveAsDraftAndVerify = useCallback(async () => {
    if (!permissions.canSubmit) {
      snackbarOpen('You do not have permission to save resource loading details.', 'error');
      return;
    }

    if (isSavingDraft) {
      return;
    }

    setIsSavingDraft(true);
    try {
      await waitForNextPaint();
      await persistResourceLoadingDraft(resourceLoadingDetailRows);
      resourceLoadingPlanCacheRef.current.clear();
      snackbarOpen('Resource loading plan verified and saved as draft.', 'success');
      void loadResourceLoadingPlan({
        requestedFromDate: fromDate,
        requestedToDate: toDate,
        preferCache: false,
        showLoader: false,
        preserveDetailRows: true,
      });
    } catch (error) {
      snackbarOpen(error?.message || 'Failed to save draft.', 'error');
    } finally {
      setIsSavingDraft(false);
    }
  }, [
    fromDate,
    isSavingDraft,
    loadResourceLoadingPlan,
    permissions.canSubmit,
    persistResourceLoadingDraft,
    resourceLoadingDetailRows,
    snackbarOpen,
    toDate,
  ]);

  const handleSubmitResourceLoadingRequest = useCallback(async () => {
    if (!permissions.canSubmit) {
      snackbarOpen('You do not have permission to submit resource loading request.', 'error');
      return;
    }

    if (isSubmittingRequest) {
      return;
    }

    setIsSubmittingRequest(true);
    try {
      await waitForNextPaint();
      await persistResourceLoadingDraft(resourceLoadingDetailRows);

      const requestUrl = buildProjectServiceApiUrl(`${RESOURCE_LOADING_BASE_PATH}/submit`);
      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          token: getStoredAuthToken(),
        },
        body: JSON.stringify({
          projectId,
          userInfo: resourceLoadingUserInfoRef.current,
        }),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseData?.message || 'Failed to submit resource loading request.');
      }

      resourceLoadingPlanCacheRef.current.clear();
      snackbarOpen(responseData?.message || 'Resource loading request submitted successfully.', 'success');
      void loadResourceLoadingPlan({
        requestedFromDate: fromDate,
        requestedToDate: toDate,
        preferCache: false,
        showLoader: false,
        preserveDetailRows: true,
      });
    } catch (error) {
      snackbarOpen(error?.message || 'Failed to submit resource loading request.', 'error');
    } finally {
      setIsSubmittingRequest(false);
    }
  }, [
    fromDate,
    isSubmittingRequest,
    loadResourceLoadingPlan,
    permissions.canSubmit,
    persistResourceLoadingDraft,
    projectId,
    resourceLoadingDetailRows,
    snackbarOpen,
    toDate,
  ]);

  const detailsTableRows = useMemo(() => {
    const totalWeekValues = {};
    const canEditDetailRow = Boolean(!permissions.isLocked && permissions.canUpdate);
    const canDeleteDetailRow = Boolean(!permissions.isLocked && permissions.canDelete);
    detailWeekFields.forEach((field) => {
      totalWeekValues[field] = 0;
    });

    const formattedDataRows = deferredVisibleResourceLoadingDetailRows.map((row) => {
      if (row.isTotalRow) {
        return row;
      }
      const formattedWeekValues = {};
      detailWeekFields.forEach((field) => {
        const num = Number(row[field]);
        const value = Number.isFinite(num) ? num : 0;
        totalWeekValues[field] += value;
        formattedWeekValues[field] = formatIndianNumber(value);
      });
      return {
        ...row,
        ...formattedWeekValues,
        totalEfforts: formatIndianNumber(Number(row.totalEfforts) || 0),
        allocationBreakdown: __buildResourceLoadingAllocationBreakdownCell(row),
        action: buildResourceLoadingDetailActionCell(
          row,
          handleOpenEditResourceLoadingDetail,
          handleOpenDeleteResourceLoadingDetail,
          canEditDetailRow,
          canDeleteDetailRow,
        ),
      };
    });

    const hasTotalRow = formattedDataRows.some((r) => r.isTotalRow);
    if (hasTotalRow || formattedDataRows.length === 0) {
      return formattedDataRows;
    }

    const grandTotalEfforts = Number(
      deferredVisibleResourceLoadingDetailRows
        .reduce((sum, r) => sum + (Number(r.totalEfforts) || 0), 0)
        .toFixed(2),
    );

    const formattedTotalWeekValues = Object.fromEntries(
      Object.entries(totalWeekValues).map(([key, value]) => [key, formatIndianNumber(value)]),
    );

    const totalRow = {
      id: 'total-row',
      loadingId: 'total-row',
      role: 'Total',
      workExperience: '',
      location: '',
      allocationBreakdown: '',
      skillsComments: '',
      totalEfforts: formatIndianNumber(grandTotalEfforts),
      persistedTotalEfforts: grandTotalEfforts,
      isTotalRow: true,
      isBottomFrozen: true,
      rowConfiguration: { isFrozen: true },
      ...formattedTotalWeekValues,
    };

    return [...formattedDataRows, totalRow];
  }, [
    detailWeekFields,
    handleOpenDeleteResourceLoadingDetail,
    handleOpenEditResourceLoadingDetail,
    permissions.canDelete,
    permissions.canUpdate,
    permissions.isLocked,
    deferredVisibleResourceLoadingDetailRows,
  ]);

  const detailsTableConfiguration = useMemo(
    () => buildReadOnlyTableConfiguration(
      enrichWeekColumnHeaders((detailsTable.tableConfiguration || []).map((column) => {
        if (column.field !== 'action') {
          return column;
        }

        const {
          type: __columnType,
          actions: __columnActions,
          ...customActionColumn
        } = column;

        return {
          ...customActionColumn,
          field: 'action',
        };
      })),
      {
        resizableColumns: false,
        tableScrollHeight: '520px',
        maxFrozenColumns: {
          desktop: 1,
          tablet: 1,
          mobile: 1,
        },
      },
    ),
    [
      detailsTable.tableConfiguration,
    ],
  );

  const handleApply = useCallback(() => {
    if (permissions.isLocked) {
      return;
    }
    if (fromDate && toDate && fromDate > toDate) {
      snackbarOpen('From date cannot be greater than to date.', 'error');
      return;
    }

    loadResourceLoadingPlan({
      requestedFromDate: fromDate,
      requestedToDate: toDate,
      preferCache: false,
    });
  }, [fromDate, loadResourceLoadingPlan, permissions.isLocked, snackbarOpen, toDate]);

  const handleOpenApprovalHistory = useCallback(() => {
    if (!projectId) {
      snackbarOpen('Project id is required to open approval history.', 'error');
      return;
    }

    window.open(
      `/delivery/project-details/resource-loading-plan/approval-history?projectId=${encodeURIComponent(projectId)}`,
      '_blank',
    );
  }, [projectId, snackbarOpen]);

  const handleOpenProjectDetails = useCallback(() => {
    if (!projectId) {
      snackbarOpen('Project id is required to open project details.', 'error');
      return;
    }

    window.open(
      `/delivery/project-details?projectId=${encodeURIComponent(projectId)}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, [projectId, snackbarOpen]);

  const exportFileName = useMemo(
    () => `Resource_Loading_Plan_${projectName || 'Project'}`,
    [projectName],
  );

  const resourceLoadingExportTables = useMemo(() => {
    const initialEstimationColumns = initialEstimationTable.tableConfiguration || [];
    const idealCategoryColumns = idealCategoryTable.tableConfiguration || [];
    const detailsColumns = detailsTable.tableConfiguration || [];
    const initialEstimationTitle = initialEstimationTable.title || 'Verification against initial estimation';
    const idealCategoryTitle = idealCategoryTable.title || 'Verification against ideal category ratio';
    const detailsTitle = 'Resource Loading Details';
    const detailExportRows = buildResourceLoadingDetailExportRows(
      deferredVisibleResourceLoadingDetailRows,
      detailWeekFields,
    );

    return [
      {
        key: 'initial-estimation',
        heading: initialEstimationTitle,
        configuration: buildResourceLoadingExportTableConfiguration({
          columns: initialEstimationColumns,
          fileName: exportFileName,
          sheetName: 'Initial Estimation',
          reportTitle: initialEstimationTitle,
          transformResponse: (rows) => buildResourceLoadingExportPayload(
            rows,
            initialEstimationColumns,
          ),
        }),
        data: initialEstimationTable.tableData || [],
      },
      {
        key: 'ideal-category-ratio',
        heading: idealCategoryTitle,
        configuration: buildResourceLoadingExportTableConfiguration({
          columns: idealCategoryColumns,
          fileName: exportFileName,
          sheetName: 'Ideal Category Ratio',
          reportTitle: idealCategoryTitle,
          transformResponse: (rows) => buildResourceLoadingExportPayload(
            rows,
            idealCategoryColumns,
            { preserveBlankNumericCells: true },
          ),
        }),
        data: idealCategoryTable.tableData || [],
      },
      {
        key: 'resource-loading-details',
        heading: detailsTitle,
        configuration: buildResourceLoadingExportTableConfiguration({
          columns: detailsColumns,
          fileName: exportFileName,
          sheetName: detailsTitle,
          reportTitle: detailsTitle,
          transformResponse: (rows) => buildResourceLoadingDetailsExportPayload(
            rows,
            detailsColumns,
          ),
        }),
        data: detailExportRows,
      },
    ];
  }, [
    detailsTable.tableConfiguration,
    deferredVisibleResourceLoadingDetailRows,
    detailWeekFields,
    exportFileName,
    idealCategoryTable.tableConfiguration,
    idealCategoryTable.tableData,
    idealCategoryTable.title,
    initialEstimationTable.tableConfiguration,
    initialEstimationTable.tableData,
    initialEstimationTable.title,
  ]);

  const exportElement = useMemo(
    () => (
      <ResourceLoadingPlanMultiExportButton
        key="resource-loading-multi-export"
        tables={resourceLoadingExportTables}
      />
    ),
    [resourceLoadingExportTables],
  );

  const toolbarElements = useMemo(() => buildToolbarElements({
    fromDate,
    toDate,
    onChangeFromDate: setFromDate,
    onChangeToDate: setToDate,
    onApply: handleApply,
    fromMaxDate: resourceLoadingPlan?.filters?.fromMaxDate || null,
    toMinDate: resourceLoadingPlan?.filters?.toMinDate || null,
    applyDisabled: Boolean(permissions.isLocked || isLoading),
    dateDisabled: false,
    exportElement,
  }), [
    exportElement,
    fromDate,
    handleApply,
    isLoading,
    permissions.isLocked,
    toDate,
    resourceLoadingPlan?.filters?.fromMaxDate,
    resourceLoadingPlan?.filters?.toMinDate,
  ]);

  const projectStatusLabel = resourceLoadingPlan?.project?.status || '';
  const projectStatusTone = resourceLoadingPlan?.project?.statusTone || '';
  const projectStatusColor = resourceLoadingPlan?.project?.statusColor || '';

  return (
    <section className="resource-loading-plan-page">
      <div className="resource-loading-plan-shell">
        {projectStatusLabel ? (
          <div className="resource-loading-plan-status-bar">
            <div className="resource-loading-plan-status-bar__content">
              <span className="resource-loading-plan-status-bar__label">
                Status:
              </span>
              <span
                className={`resource-loading-plan-status-bar__value resource-loading-plan-status-bar__value--${projectStatusTone || 'default'}`}
                style={projectStatusColor ? { color: projectStatusColor } : undefined}
              >
                {projectStatusLabel}
              </span>
            </div>
          </div>
        ) : null}
        <div className="resource-loading-plan-table-utility resource-loading-plan-table-utility--toolbar">
          <div className="card dashboard-container">
            <div className="project-overview-header">
              <div className="toolbar-heading">
                <span className="resource-loading-plan-toolbar-heading">
                  <span>Resource Loading Plan</span>
                  {resolvedProjectName ? (
                    <>
                      <span className="resource-loading-plan-toolbar-heading__separator">-</span>
                      <button
                        type="button"
                        className="resource-loading-plan-toolbar-heading__project-link"
                        onClick={handleOpenProjectDetails}
                        title="Open project details"
                      >
                        {resolvedProjectName}
                      </button>
                    </>
                  ) : null}
                </span>
              </div>
              <div className="button-group">{toolbarElements}</div>
            </div>
          </div>
        </div>

        <ResourceLoadingPlanBody
          initialEstimationTable={initialEstimationTable}
          idealCategoryTable={idealCategoryTable}
          detailsTableRows={detailsTableRows}
          detailsTableConfiguration={detailsTableConfiguration}
          isLoading={isLoading}
          permissions={permissions}
          handleOpenApprovalHistory={handleOpenApprovalHistory}
          handleAddResourceLoadingDetail={handleOpenAddResourceLoadingDetail}
        />

        {permissions?.canSubmit ? (
          <div className="resource-loading-plan-footer">
            <button
              type="button"
              className="resource-loading-footer-btn-link"
              onClick={handleSaveAsDraftAndVerify}
              disabled={Boolean(permissions?.isLocked) || isSavingDraft || isSubmittingRequest}
            >
              {isSavingDraft ? 'Saving...' : 'Save as Draft & Verify'}
            </button>
            <button
              type="button"
              className="resource-loading-footer-btn-primary"
              onClick={handleSubmitResourceLoadingRequest}
              disabled={Boolean(permissions?.isLocked) || isSavingDraft || isSubmittingRequest}
            >
              {isSubmittingRequest ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        ) : null}
      </div>

      <ResourceLoadingDetailModal
        isOpen={detailModalState.isOpen}
        mode={detailModalState.mode}
        initialRow={detailModalState.row}
        canManageAllocations={detailModalState.mode === 'edit'
          ? Boolean(permissions.canUpdate)
          : Boolean(permissions.canCreate)}
        canDeleteAllocations={Boolean(permissions.canDelete)}
        roleOptions={roleOptions}
        workExperienceOptions={workExperienceOptions}
        roleExperienceMappings={roleExperienceMappings}
        locationOptions={locationOptions}
        projectStartDate={resourceLoadingPlan?.filters?.projectStartDate || ''}
        projectEndDate={resourceLoadingPlan?.filters?.projectEndDate || ''}
        onClose={handleCloseDetailModal}
        onSubmit={handleSubmitResourceLoadingDetail}
        onValidationMessage={snackbarOpen}
      />

      <ResourceLoadingDetailDeleteConfirm
        isOpen={deleteConfirmState.isOpen}
        title="Delete Request"
        message="Are you sure you want to delete this request?"
        onClose={() => setDeleteConfirmState({
          isOpen: false,
          rowKey: null,
          row: null,
        })}
        onConfirm={handleConfirmDeleteResourceLoadingDetail}
      />
    </section>
  );
};

export default ResourceLoadingPlan;
